/*
 * Kidslar guided quiz funnel
 * ---------------------------------------------------------------------------
 * Self-contained, no dependencies. Structure:
 *   1. QUIZ DATA      — approved, frozen customer-facing copy (do not edit)
 *   2. SCORING        — pure functions, testable in isolation
 *   3. STATE          — localStorage persistence + attribution
 *   4. ANALYTICS      — CustomEvent + dataLayer + fbq trackCustom
 *   5. CART           — native /cart/add.js + theme cart drawer
 *   6. RENDER         — screen renderers
 *   7. CONTROLLER     — KidslarQuiz element wiring + boot
 *
 * Merchant-editable result copy lives in the section schema, not here.
 */
(function (global) {
  'use strict';

  /* ========================================================================
   * 1. QUIZ DATA — APPROVED COPY. DO NOT REWRITE, SHORTEN OR "IMPROVE".
   * Internal `category` / `tag` values are logic-only and never shown.
   * Branch-keyed so further branches can be added without touching the engine.
   * ====================================================================== */

  var CATEGORIES = ['kalem', 'okunaklilik', 'istek', 'ozguven'];

  // Deterministic fallback order (spec step 4). Never randomised.
  var FALLBACK_PRIORITY = ['istek', 'ozguven', 'okunaklilik', 'kalem'];

  // Questions that feed the observation score (spec step 1).
  var OBSERVATION_QUESTIONS = ['q2', 'q3', 'q4', 'q5'];

  var BRANCHES = {
    ilkokul: {
      id: 'ilkokul',
      questions: [
        {
          id: 'q1',
          type: 'single',
          role: 'hypothesis', // no diagnostic points, tie-breaker only
          title: 'Çocuğunuz en çok nerede zorlanıyor?',
          helper: 'Bu tahmininizi, cevaplarınızdan çıkan sonuçla karşılaştıracağız.',
          options: [
            { id: 'q1a', category: 'kalem', text: 'Biraz yazınca eli yoruluyor, hemen bırakmak istiyor' },
            { id: 'q1b', category: 'okunaklilik', text: 'Yazısı okunmuyor, bazen kendi bile okuyamıyor' },
            { id: 'q1c', category: 'istek', text: 'Yazı yazarken çabuk sıkılıyor, devam etmek istemiyor.' },
            { id: 'q1d', category: 'ozguven', text: 'Daha başlamadan yapamayacağını düşünüyor, hemen vazgeçiyor.' }
          ]
        },
        {
          id: 'q2',
          type: 'single',
          role: 'observation',
          title: 'Bir sayfa yazının sonuna doğru genelde hangisi oluyor?',
          helper: 'İkisi de oluyorsa, daha sık olanı seçin.',
          // Two options per category. Scoring is per selected option's category,
          // so a category still receives exactly +1 no matter which of its two
          // options is picked. Ids q2a/q2b keep their original meaning so
          // sessions saved before this change still resolve correctly.
          options: [
            {
              id: 'q2a',
              category: 'kalem',
              text: 'Sayfanın yarısına gelmeden “elim ağrıdı” diyor ve mola vermek istiyor'
            },
            {
              id: 'q2c',
              category: 'kalem',
              text: 'Yazdıkça kalemi daha sıkı tutuyor, eli çabuk yoruluyor'
            },
            {
              id: 'q2b',
              category: 'okunaklilik',
              text: 'Harfleri bir büyük bir küçük yazıyor, satırın dışına taşıyor.'
            },
            {
              id: 'q2d',
              category: 'okunaklilik',
              text: 'Sayfanın sonuna doğru harfler daha da dağınık ve okunaksız oluyor.'
            }
          ]
        },
        {
          id: 'q3',
          type: 'single',
          role: 'observation',
          title: 'Yazı yazmasını istediğinizde genelde nasıl tepki veriyor?',
          helper: 'İkisi de oluyorsa, daha sık olanı seçin.',
          options: [
            { id: 'q3a', category: 'istek', text: '“Birazdan” diyor, sürekli oyalanıp başlamıyor.' },
            { id: 'q3b', category: 'ozguven', text: '“Ben yapamam” deyip daha başlamadan pes ediyor.' }
          ]
        },
        {
          id: 'q4',
          type: 'single',
          role: 'observation',
          title: 'Ödevini bitirmeden bıraktığında genelde ne oluyor?',
          helper: 'İkisi de oluyorsa, daha sık olanı seçin.',
          options: [
            { id: 'q4a', category: 'kalem', text: 'Elini dinlendirmek istiyor, biraz sonra tekrar devam ediyor.' },
            { id: 'q4b', category: 'istek', text: 'Başka bir şeyle uğraşmaya geçiyor, ödeve dönmek istemiyor.' }
          ]
        },
        {
          id: 'q5',
          type: 'single',
          role: 'observation',
          title: 'Yazdığını beğenmeyip sildirdiğinizde sonrasında ne oluyor?',
          helper: 'Kızmak istemediğinizi biliyoruz; sadece ne olduğunu anlamaya çalışıyoruz.',
          options: [
            { id: 'q5a', category: 'okunaklilik', text: 'İki satır düzeltiyor, sonra yine eski yazısına dönüyor.' },
            { id: 'q5b', category: 'ozguven', text: 'Morali bozuluyor, o gün bir daha yazmak istemiyor.' }
          ]
        },
        {
          id: 'q6',
          type: 'multi',
          role: 'context',
          title: 'Yazısı için neler denediniz?',
          helper: 'Birden fazla seçebilirsiniz.',
          options: [
            {
              id: 'q6a',
              tag: 'tutor_failed',
              text: 'Özel öğretmen tuttuk, yine de istediğimiz sonucu alamadık.'
            },
            {
              id: 'q6b',
              tag: 'parent_practice_failed',
              text: 'Akşamları ben çalıştırdım ama ne kadar uğraşsak da istediğimiz sonucu alamadık.'
            },
            {
              id: 'q6c',
              tag: 'products_failed',
              text: 'Başka defterler, setler de aldım; hevesi çabuk kaçtı, yazısında da bir değişiklik olmadı.'
            },
            {
              id: 'q6d',
              tag: 'school_work_failed',
              text: 'Okuldan verilen ek çalışmaları da yaptırdım ama yeterli olmadı.'
            },
            {
              id: 'q6e',
              tag: 'no_consistent_attempt',
              text: 'Açıkçası düzenli olarak bir şey yaptıramadım.'
            }
          ]
        },
        {
          id: 'q7',
          type: 'multi',
          role: 'context',
          title: 'Bu konuyu artık neden çözmek istiyorsunuz?',
          helper: 'Birden fazla seçebilirsiniz.',
          options: [
            {
              id: 'q7a',
              tag: 'teacher_trigger',
              text: 'Öğretmeni söyleyince kafama takıldı, o günden beri düşünüyorum.'
            },
            {
              id: 'q7b',
              tag: 'notebook_trigger',
              text: 'Defterini görünce içime sinmedi, bir şey yapmam gerektiğini düşündüm.'
            },
            { id: 'q7c', tag: 'delayed_too_long', text: 'Çok uzattım, artık ertelemek istemiyorum.' },
            { id: 'q7d', tag: 'school_urgency', text: 'Okul açılmadan toparlansın istiyorum.' }
          ]
        },
        {
          id: 'q8',
          type: 'single',
          role: 'desire', // first tie-breaker
          title: 'Üç hafta sonra hangisini görseniz içiniz rahat eder?',
          helper: '',
          options: [
            { id: 'q8a', category: 'kalem', text: '“Yoruldum” demeden bir sayfa bitirsin' },
            { id: 'q8b', category: 'okunaklilik', text: 'Öğretmeni “Yazısı gerçekten çok düzelmiş” desin.' },
            { id: 'q8c', category: 'istek', text: 'Yazı zamanı geldiğinde tartışmadan başlayabilsin.' },
            { id: 'q8d', category: 'ozguven', text: '“Anne bak, ben yazdım” diye koşup gelsin' }
          ]
        },
        {
          id: 'q9',
          type: 'single',
          role: 'need',
          title: 'Şimdi en çok neye ihtiyacınız var?',
          helper:
            'Buraya kadar geldiğinize göre çocuğunuz için gerçekten uğraşıyorsunuz. Öneriyi ihtiyacınıza göre kuracağız.',
          options: [
            { id: 'q9a', tag: 'clear_plan', text: 'Ne yapacağımı adım adım anlatan bir yöntem.' },
            { id: 'q9b', tag: 'independence', text: 'Ben yanında oturmadan kendi başına yapabileceği bir şey.' },
            { id: 'q9c', tag: 'visible_progress', text: 'Yazısında birkaç hafta içinde gözle görülür bir gelişme.' },
            {
              id: 'q9d',
              tag: 'risk_reversal',
              text: 'Bir daha para verip uğraşıp yine sonuçsuz kalmak istemiyorum.'
            }
          ]
        }
      ]
    }
  };

  /* ------------------------------------------------------------------------
   * STAGED RESULT FLOWS — APPROVED COPY. DO NOT REWRITE OR SHORTEN.
   *
   * A category listed here replaces the single-page result with a sequence of
   * screens the parent steps through. Categories with no entry keep the
   * existing single-page result untouched.
   * ---------------------------------------------------------------------- */
  var RESULT_FLOWS = {
    okunaklilik: [
      {
        id: 'problem',
        type: 'statement',
        headline: 'Cevaplarınıza göre sorun sadece dağınık yazı değil.',
        support: 'Ne kadar yazarsa yazsın, aynı hatalar tekrar ediyor.',
        label: 'Sizde öne çıkan durum',
        problem:
          'Harfleri bir büyük bir küçük yazıyor, satırın dışına taşıyor. Düzelttiğinde birkaç satır daha iyi yazıyor ama sonra yine eski haline dönüyor.',
        realization: 'Sadece daha fazla yazdırmak bu sorunu tek başına çözmeyebilir.',
        cta: 'Sorunun nedenini görün'
      },
      {
        id: 'mechanism_video',
        type: 'video',
        eyebrow: 'NEDEN TEKRAR EDİYOR?',
        videoKey: 'karalama',
        videoAlt:
          'Bir çocuk çizgili deftere yazı yazıyor; harfler bir büyük bir küçük çıkıyor ve satırın dışına taşıyor.',
        // Explanation sits below the video, never inside its container.
        headline: 'Sorun ne kadar yazdığı değil, nasıl yazdığı.',
        paragraphs: [
          'Çocuk harfi yanlış hareketle yazmaya alıştıysa, daha fazla tekrar yapmak aynı hatayı daha çok tekrar etmek anlamına gelebilir.',
          'Normal bir defter harfin nasıl görünmesi gerektiğini gösterir. Ama kalemin nereden başlayıp hangi yönde hareket etmesi gerektiğini göstermez.'
        ],
        cta: 'Doğru yöntemi görün'
      },
      {
        id: 'method',
        type: 'content',
        eyebrow: 'PEKİ DOĞRU YÖNTEM NE?',
        headline:
          'Çocuğa sadece harfi göstermek yetmez. Doğru kalem hareketini de öğretmek gerekir.',
        // Authored copy. `html` is trusted, static and never user-supplied.
        blocks: [
          {
            type: 'p',
            html: 'Normal defterde çocuk harfi görür ve kendi bildiği gibi yazar.'
          },
          {
            type: 'p',
            html: '<strong>Kidslar El Yazı Eğitim Seti’nin oluklu sayfaları kaleme doğru yolu gösterir.</strong>'
          },
          {
            // Proof of the sentence directly above it. Served straight from the
            // Shopify CDN — not copied into theme assets, not re-encoded.
            type: 'video',
            src: 'https://cdn.shopify.com/videos/c/o/v/44b5fc75c79747e3bf18417d2cfb8b0c.mp4',
            aspect: '1 / 1',
            alt: 'Kalem, oluklu sayfadaki belirlenen yolun içinde ilerleyerek harfi takip ediyor.'
          },
          {
            type: 'p',
            html: 'Çocuk harfin nereden başlayacağını ve hangi yönde ilerleyeceğini <strong>eliyle takip eder.</strong>'
          },
          {
            type: 'quote',
            html: 'Amaç daha çok yazdırmak değil. Doğru hareketi tekrar ettirmek.'
          }
        ],
        cta: 'Kidslar’ın nasıl çalıştığını görün',
        ctaArrow: '→'
      },
      {
        id: 'recommendation',
        type: 'recommendation',
        eyebrow: 'SİZE ÖNERİMİZ',
        headline:
          'Cevaplarınıza göre çocuğunuzun ihtiyacı daha çok yazmak değil, doğru şekilde yazmayı tekrar etmek.',
        bridge: [
          'Çocuk harfi yanlış şekilde yazmaya alıştıysa, daha fazla yazmak aynı yanlışı daha fazla tekrar ettirir.',
          'Asıl önemli olan, harfi doğru yerden başlayıp doğru yönde yazmayı tekrar etmektir.',
          'Kidslar’ın oluklu sayfaları kalemi bu doğru yolun içinde tutarak çocuğun doğru harf hareketini tekrar etmesine yardımcı olur.'
        ],

        // Illustrative comparison of the two page types, not customer proof.
        // Labelled as such, and deliberately placed above the expert and
        // parent proof so an illustration never sits next to a real review.
        compare: {
          note: 'El yazısındaki gerçek gelişim',
          // Single caption under both images, not one per image.
          caption: 'Kidslar ile 21 gün kullanımdan sonraki gelişim',
          items: [
            {
              imageKey: 'serbest',
              alt: 'Çizgili düz defter sayfasına elle yazılmış, harf boyları ve satır düzeni dağınık bir metin.'
            },
            {
              imageKey: 'oluklu',
              alt: 'Kidslar oluklu çalışma sayfası; her satırda kalemin takip edeceği belirlenmiş bir yol var.'
            }
          ]
        },

        expertsHeading: 'Uzmanlar Kidslar’ın bu yöntemi hakkında ne diyor?',
        // Attributed testimonials, quoted exactly. Never paraphrased into
        // brand claims. `imageKey` resolves to a real store asset or nothing —
        // a card with no verified photo simply renders without one. `tag` is
        // an editorial label naming the doubt this particular quote answers;
        // it is not a claim and is not attributed to the expert.
        experts: [
          {
            tag: 'Yazıda gerçekten fark olur mu?',
            quote:
              'Sınıfımda yazısı bozuk olan çocuklarda kısa sürede belirgin fark görüyorum. Özellikle yeni öğrenenlerde sonuç çok hızlı.',
            name: 'Nur H.',
            title: 'Öğretmen',
            imageKey: 'nur'
          },
          {
            tag: 'Oluklu yapı gerçekten farklı mı?',
            quote:
              'Çocukların kalem tutuş ve harf yönü hataları kalıcı hale gelmeden müdahale etmek lazım. Oluklu yapı bunu fiziksel olarak çözüyor — pedagojik açıdan çok değerli.',
            name: 'Zübeyde A.',
            title: 'Pedagog',
            imageKey: 'zubeyde'
          },
          {
            tag: 'Bu sadece normal bir defter mi?',
            quote:
              'Dokunsal rehberlik, ince motor becerilerini destekleyerek çocukların öğrenme süreçlerine somut katkı sağlıyor. Aktif olarak kullanıyorum.',
            name: 'DKT Yeliz Rabia Uzkurt',
            title: 'Dil ve Konuşma Terapisti · İzmir',
            imageKey: 'yeliz'
          },
          {
            tag: 'Çocuk yazarken geriliyorsa?',
            quote:
              'Oluklu yapı doğru kas hafızasının oluşmasına net şekilde yardımcı oluyor. Yazı kaygısı yaşayan danışanlarımda silinen mürekkep duygusal rahatlama sağlıyor.',
            name: 'Klinik Psk. Tutku Damla Topaloğlu',
            title: 'Klinik Psikolog',
            imageKey: 'tutku'
          }
        ],

        // Q9 personalisation. Each entry follows the parent's own answer, which
        // is quoted verbatim above it. No timeframes are promised.
        needCopy: {
          clear_plan:
            'Sette harfler belirli bir sırayla ilerliyor. Hangi sayfayı ne zaman çalışacağınızı siz kurgulamak zorunda kalmıyorsunuz; sıra hazır geliyor.',
          independence:
            'Oluklu yol kalemi kendisi yönlendirdiği için her harfte yanında oturup düzeltmeniz gerekmiyor. Çocuk kendi başına çalışırken siz sürekli müdahale eden kişi olmaktan çıkıyorsunuz.',
          visible_progress:
            '21 gün düzenli kullanan velilerin %92’si, çocuğunun yazısında olumlu bir değişim görüyor.\nBu yüzden 21 günde gözle görülür gelişim garantisi veriyoruz.',
          risk_reversal: null // handled by the store's real return policy below
        },

        cta: 'Okula Dönüş Kampanyasını ve Stokları Kontrol Et'
      },
      /* ----------------------------------------------------------------
       * OFFER SCREEN — the funnel's conversion page.
       *
       * EVERY factual value below (price, compare-at, rating, review count,
       * set contents, guarantee terms, shipping, FAQ answers) was read from
       * the LIVE PDP + store policy pages on 2026-08-11:
       *   kidslar.com/products/kidslar-kabartmali-defter-seti (+ .js)
       *   kidslar.com/policies/refund-policy
       *   kidslar.com/policies/shipping-policy
       * Pricing/rating/stock render from `config.product` (Shopify objects),
       * not from anything hardcoded here. Do not invent, round, or "improve"
       * any number, claim or guarantee condition in this block.
       * ---------------------------------------------------------------- */
      {
        id: 'offer',
        type: 'offer',
        eyebrow: 'OKULA DÖNÜŞ KAMPANYASI',
        headline: 'Çocuğunuzun El Yazısı İçin İhtiyacı Olan Her Şey Tek Sette',
        productName: 'Kidslar El Yazı Eğitim Seti',
        /* Positioning line, deliberately demoted: it now renders as a small
           tag beside the product name instead of a second eyebrow above the
           headline, where it was competing with the campaign label. */
        systemLabel: '21 Günlük El Yazısı Gelişim Sistemi',

        // Hero offer stack — exactly the four things the offer contains.
        // No additional bonuses may be added here.
        heroStack: [
          { type: 'core', text: 'Kidslar El Yazı Eğitim Seti' },
          { type: 'free', text: 'ÜCRETSİZ Kalem Seti' },
          { type: 'free', text: 'ÜCRETSİZ 21 Günlük Ebeveyn Rehberi (PDF)' },
          { type: 'guarantee', text: '21 Gün Gelişim Garantisi' }
        ],
        cta: '21 Günlük Gelişime Başla',

        /* --- REAL package tiers -------------------------------------------
         * These are the merchant's own PDP packages, and every price below
         * was VERIFIED on 2026-08-11 by adding that quantity to the live
         * cart and reading /cart.js back. The savings come from a real
         * Shopify AUTOMATIC discount ("Magic Learn Seti — paket indirimi"),
         * which applies from any page purely on quantity — no app, no code,
         * no discount code. Measured:
         *   qty 1  -> 899,99      (no bundle discount)
         *   qty 2  -> 1.499,98    (−300,00   | 749,99/set)
         * `wasAmount` is qty x the single-set campaign price, which is what
         * the same quantity costs without the bundle discount — the exact
         * comparison the live PDP makes. Do not re-label or re-price any of
         * this without re-verifying against the live cart.
         *
         *   qty 4  -> 2.599,96    (−1.000,00 | 649,99/set)
         * These are three of the live PDP's own four bundle bars, with the
         * merchant's own names. The 7-set "Sınıf Paketi" is left off: it was
         * written for teachers and this funnel's traffic is parents buying
         * for their own child. `perSet` is only carried on a tier where it
         * differs from the headline price — on a single set it would just
         * repeat the number.
         *
         * ribbons: "Set başına en avantajlı" on the 4-set is an ECONOMICS
         * claim the measured numbers back — 649,99/set really is the lowest
         * of the three. "En çok tercih edilen" on the 2-set is a POPULARITY
         * claim I cannot verify; it is reused verbatim from the live PDP's
         * own bar, which is the merchant's claim on the merchant's store.
         * ------------------------------------------------------------------ */
        packages: [
          {
            id: 'tek',
            qty: 1,
            label: 'Tek Set',
            amount: 89999
          },
          {
            id: 'kardes',
            qty: 2,
            label: 'Kardeş Paketi',
            // Verbatim from the live PDP's own bar for this tier.
            ribbon: 'EN ÇOK TERCİH EDİLEN',
            recommended: true,
            amount: 149998,
            wasAmount: 179998,
            savesAmount: 30000,
            perSet: '749,99 TL'
          },
          /* The live PDP's "Geniş Aile Paketi" bar, matched exactly. Read off
             the PDP widget AND re-measured in the live cart on 2026-08-11:
             quantity 4 returns total 259996, original_line_price 359996, and
             a line-level allocation of 100000 titled "Magic Learn Seti —
             paket indirimi". PDP bar shows the same 2.599,96 / 3.599,96.
             Nothing here is computed.

             This replaced a 3-set tier I had built from the same automatic
             discount. The store has no 3-set bar, and at 749,99/set a 3-set
             was no cheaper per set than the 2-set — so it could not honestly
             be called "fazla al az öde". The 4-set can: 649,99/set. */
          {
            id: 'aile',
            qty: 4,
            /* Customer-facing name only. The tier, its quantity and every
               number below are unchanged — this is the back-to-school
               campaign dressing on the same 4-set bar. `eyebrow` renders as
               the small green label above the name, `label` as the name
               itself, and `label` is what the sticky bar and the recap read. */
            eyebrow: 'OKULA DÖNÜŞ KAMPANYASI',
            label: 'Fazla Al Az Öde',
            // Economics claim, and the measured per-set prices support it.
            ribbon: 'SET BAŞINA EN AVANTAJLI',
            amount: 259996,
            wasAmount: 359996,
            savesAmount: 100000,
            perSet: '649,99 TL'
          }
        ],
        /* Two lines: the campaign name as a small upper label, the offer
           itself as the heading the parent actually reads. */
        packagesEyebrow: 'OKULA DÖNÜŞ KAMPANYASI',
        packagesHeading: 'Fazla Al Az Öde',

        /* Gifts that ride along with EVERY package, rendered as picture cards.
           `imageKey` resolves at render time against `config.componentImages`,
           which the section fills with REAL store files — the pen set's own
           product photo and the guide's own printed cover. Nothing here points
           at invented or AI-made art, and no gift may be listed that the order
           does not actually ship. */
        bonusHeading: 'Bugün ücretsiz',
        bonuses: [
          {
            id: 'kalem',
            imageKey: 'kalem',
            tag: 'HEDİYE',
            name: 'Ücretsiz Kalem Seti',
            /* No "X TL değerinde" line: the same pen set is sold on the store
               right now at 199,99 (compare-at 349,99), so quoting either
               number as "the value of your gift" would be a claim the live
               store contradicts. The photo carries the value on its own. */
            note: 'Silinebilir mürekkep'
          },
          {
            id: 'rehber',
            imageKey: 'rehber',
            tag: 'HEDİYE',
            // Matches the guide's own cover: "21 GÜNLÜK EL YAZISI REHBERİ".
            name: '21 Günlük El Yazısı Eğitim Rehberi',
            note: 'PDF'
          }
        ],
        /* One reassurance strip, not a third gift card — the guarantee is
           protection, and the long version stays further down the page. */
        guaranteeStrip: {
          title: '21 Gün Gelişim Garantisi',
          text: '21 gün içinde gelişim görmezseniz garanti kapsamında paranızı geri alın.'
        },
        /* Deliberately short: two checks under the CTA, nothing more.
           Delivery is 1-2 iş günü per the merchant (2026-08-11). NOTE: the
           store's own /policies/shipping-policy page still says "2 ila 4 iş
           günü" — the policy page is the one a customer can quote back, so it
           needs updating to match. Flagged. */
        reassurance: ['Güvenli ödeme (PayTR)', '1-2 iş gününde teslimat'],

        // Only rendered when live Shopify pricing actually produces the
        // percentage this label claims — buildOfferPrice recomputes it from
        // price vs compare_at and drops the label if the maths disagrees.
        campaignLabel: '%45 Okula Dönüş Kampanyası',

        /* --- SECTION 2: what you get (short) --------------------------- */
        contentsHeading: '21 Günlük sistemin içinde neler var?',
        // Component names are verbatim from the live PDP "Sette Ne Var" block.
        // `imageKey` points at THAT component's own photo (the notebook's own
        // printed cover, the pen set's own product shot, the guide's own
        // cover) — never the full-set composite standing in for a part of
        // itself. The picture carries the explanation, so each card gets one
        // short clause and nothing more.
        contents: [
          { imageKey: 'alfabe', badge: 'Ana Sistem', name: 'Alfabe Defteri', why: 'Harfleri doğru hareketle çalışmak için.' },
          { imageKey: 'matematik', badge: 'Ana Sistem', name: 'Matematik Defteri', why: '0-100 sayı yazımı ve işlemler için.' },
          { imageKey: 'cizgi', badge: 'Ana Sistem', name: 'Çizgi Defteri', why: 'Kalem tutuşu ve ince motor için.' },
          { imageKey: 'kalem', badge: 'Hediye', highlight: true, name: 'Sihirli Kalem Seti', why: 'Aynı sayfayı sınırsız tekrar etmek için.' },
          // Same name as the gift card in the buy box — one gift, one name.
          { imageKey: 'rehber', badge: 'Hediye', highlight: true, name: '21 Günlük El Yazısı Eğitim Rehberi', why: 'Her gün ne çalışacağınızı bilmek için.' }
        ],

        /* --- SECTION 4: guarantee --------------------------------------- */
        guarantee: {
          heading: '21 Gün Gelişim Garantisi',
          // Verbatim promise from the live PDP guarantee block.
          promise:
            'Kidslar’ı 21 gün düzenli kullanın. Çocuğunuzun el yazısında gelişme görmezseniz, garanti kapsamında paranızı geri alın.',
          steps: [
            '21 gün düzenli kullanın',
            'Yazısındaki gelişimi gözlemleyin',
            'Gelişim yoksa garanti kapsamında başvurun'
          ],
          // Real conditions, from kidslar.com/policies/refund-policy.
          // Do not soften into "koşulsuz" or "sorusuz" — the policy does
          // carry conditions, and they are listed here as written.
          /* `strong: true` marks the one condition parents actually worry
             about — "we opened it and used it, so we're stuck with it". It
             renders bold, in plain language, because a policy sentence that
             is technically correct but has to be decoded is doing nothing to
             reverse the risk. The policy meaning is unchanged. */
          conditions: [
            'Süre ürünü teslim aldığınız tarihten itibaren 21 gündür.',
            {
              strong: true,
              text: 'Ambalajı açmış, defterleri kullanmış olsanız bile iade edebilirsiniz. Denemiş olmanız iadeye engel değil.'
            },
            'Setin ana parçalarının iade paketinde bulunması gerekir.',
            'İade kargosu ücretsizdir; iade kodu veya etiketi ekibimiz tarafından iletilir.',
            'Kullanımdan kaynaklanan kırılma, kayıp veya olağan kullanımın ötesindeki hasarlar kapsam dışıdır.'
          ],
          /* WhatsApp instead of an email address: it is the channel the store
             already runs support on (the same number the refund policy page
             publishes), and a tappable button is a lower-effort ask than
             "compose an email" at the exact moment we are asking a hesitant
             parent to trust us. */
          howTo: 'İade talebiniz için müşteri destek ekibimize WhatsApp’tan yazmanız yeterli.',
          howToAction: {
            label: 'WhatsApp’tan iade talebi oluştur',
            // kidslar.com/policies/refund-policy publishes this same number.
            url: 'https://wa.me/902626060794?text=' + encodeURIComponent('Merhaba, siparişim için iade talebi oluşturmak istiyorum. Sipariş numaram: '),
            note: 'Sipariş numaranızı yazmanız yeterli.'
          },
          legalNote:
            'Bu memnuniyet garantisi, tüketici olarak sahip olduğunuz yasal hakları sınırlamaz.',
          // Already approved and in use in this same funnel (Q9
          // visible_progress copy). Not introduced independently here.
          stat: '21 gün düzenli kullanan velilerin %92’si, çocuğunun yazısında olumlu bir değişim görüyor.',
          statFollow: 'Bu yüzden 21 günde gözle görülür gelişim garantisi veriyoruz.'
        },

        /* --- SECTION 3: compact customer proof -------------------------- */
        reviewsHeading: 'Aynı soruyu soran aileler ne yaşadı?',
        // Five of the store's real reviews, chosen for the doubts that stop
        // a purchase. Labels are editorial framing over REAL review text
        // pulled live from the product's review metafield; no text, name,
        // star or badge is invented. reviewLimit keeps this compact — the
        // funnel already showed the full proof section earlier.
        reviewLimit: 5,
        reviewObjections: [
          { author: 'Meltem Özkan', label: 'Gerçekten fark oluyor mu?' },
          {
            author: 'Elif Yıldırım',
            label: 'Yazısı düzelir mi?',
            // Display-only excerpt: first three sentences, verbatim and in
            // original order. The live review text is untouched.
            excerpt:
              'Kızım harfleri bir türlü düzgün yazamıyordu. Bu defterler sayesinde kısa sürede okunaklı yazmaya başladı. Öğretmenleri bile gelişimi fark etti ve takdir etti.'
          },
          { author: 'Cemal Usta', label: 'Daha önce başka şeyler denedik' },
          { author: 'Ahmet Kaya', label: 'Çocuğum yazmayı sevmiyor' },
          { author: 'Ayşe Güneş', label: 'Evde kendi başımıza kullanabilir miyiz?' }
        ],

        /* --- SECTION 5: final buy box ---------------------------------- */
        // The recap renders the same `bonuses` the hero does, in compact
        // form — there is no second list to keep in sync with the first.
        recapHeading: 'Bugün siparişinizde:',

        /* --- SECTION 6: FAQ -------------------------------------------- */
        // Questions and answers are the live PDP's own FAQ, reused as-is.
        // The delivery/COD answer follows the store's shipping policy page
        // where the PDP FAQ and the policy disagreed (policy wins).
        faqHeading: 'Sıkça sorulanlar',
        faq: [
          {
            q: 'Kaç yaş için uygun?',
            a: '3-9 yaş arası çocuklar için tasarlandı. Anasınıfı, 1-2-3. sınıf ve yazısı bozulan tüm çocuklarda etkili. Disgrafi, ADHD ve dikkat dağınıklığı yaşayan çocuklarda psikolog önerisiyle de kullanılır.'
          },
          {
            q: 'Setin içinde tam olarak neler var?',
            a: 'Set 3 defterden oluşur: Alfabe defteri (el yazısı), matematik defteri (sayı becerisi), çizgi defteri (ince motor + el-göz koordinasyonu). Ayrıca sihirli kalem seti + ergonomik kalem tutucu + yedek mürekkep ile birlikte gelir. Sipariş onayıyla birlikte ücretsiz Kidslar Ebeveyn Rehberi (PDF) de e-posta ile gönderilir.'
          },
          {
            q: '21 Gün Gelişim Garantisi nasıl çalışıyor?',
            // Same channel as the guarantee block — one route for returns.
            a: 'Ürünü teslim aldıktan sonra 21 gün boyunca deneyin. Yazıda belirgin gelişim görmezseniz, ambalaj açılmış olsa bile iade talebi oluşturabilirsiniz. Ücretsiz iade kargo kodu veya etiketi ekibimiz tarafından iletilir. Talebinizi sipariş numaranızla WhatsApp destek hattımıza iletebilirsiniz.'
          },
          {
            q: 'Yazılar ne kadar sürede siliniyor?',
            a: 'Sihirli mürekkep yazıldıktan 10-15 dakika sonra kendiliğinden silinir. Sayfalar binlerce kez kullanılabilir. Ortalama 1-2 yıl aktif kullanım.'
          },
          {
            q: '21 Günlük Ebeveyn Rehberi nasıl kullanılıyor?',
            a: 'Sipariş onayıyla birlikte e-posta ile gönderilir. Çocuğunuzla evde nasıl çalışacağınızı, hangi defteri ne zaman kullanacağınızı ve 21 günlük programı adım adım anlatır. Ayrıca öğretmen desteği gerekmez; oluklu yapı çocuğun elini doğru harekete yönlendirir.'
          },
          {
            q: 'Siparişim ne zaman gelir?',
            a: 'Siparişler genellikle 1-2 iş gününde teslim edilir. Online ödemede kargo ücretsizdir. Kapıda nakit veya kredi kartıyla da ödeyebilirsiniz; kapıda ödemede kargo hizmet bedeli sipariş özetinde ayrıca gösterilir.'
          }
        ]
      }
    ]
  };

  /* ========================================================================
   * 2. SCORING — pure functions (no DOM, no storage)
   * ====================================================================== */

  function getBranch(branchId) {
    return BRANCHES[branchId] || BRANCHES.ilkokul;
  }

  function findQuestion(branch, questionId) {
    var questions = branch.questions;
    for (var i = 0; i < questions.length; i++) {
      if (questions[i].id === questionId) return questions[i];
    }
    return null;
  }

  function findOption(branch, questionId, optionId) {
    var question = findQuestion(branch, questionId);
    if (!question || !optionId) return null;
    for (var i = 0; i < question.options.length; i++) {
      if (question.options[i].id === optionId) return question.options[i];
    }
    return null;
  }

  function categoryOf(branch, questionId, optionId) {
    var option = findOption(branch, questionId, optionId);
    return option && option.category ? option.category : null;
  }

  function tagOf(branch, questionId, optionId) {
    var option = findOption(branch, questionId, optionId);
    return option && option.tag ? option.tag : null;
  }

  /**
   * Observation score — Q2..Q5 only, +1 per selected category.
   * Each category competes exactly twice, so the range is 0..2.
   */
  function scoreObservations(answers, branchId) {
    var branch = getBranch(branchId);
    var scores = {};
    CATEGORIES.forEach(function (category) {
      scores[category] = 0;
    });

    OBSERVATION_QUESTIONS.forEach(function (questionId) {
      var category = categoryOf(branch, questionId, answers[questionId]);
      if (category && Object.prototype.hasOwnProperty.call(scores, category)) {
        scores[category] += 1;
      }
    });

    return scores;
  }

  /**
   * Full diagnostic algorithm.
   *   step 1 observation score → unique max wins
   *   step 2 Q8 tie-breaker
   *   step 3 Q1 tie-breaker
   *   step 4 fixed priority: istek → ozguven → okunaklilik → kalem
   * Deterministic by construction; never random.
   */
  function diagnose(answers, branchId) {
    var branch = getBranch(branchId);
    var scores = scoreObservations(answers, branchId);

    var highest = CATEGORIES.reduce(function (max, category) {
      return Math.max(max, scores[category]);
    }, 0);

    var tied = CATEGORIES.filter(function (category) {
      return scores[category] === highest;
    });

    if (tied.length === 1) {
      return { category: tied[0], scores: scores, highest: highest, tied: tied, resolvedBy: 'observation' };
    }

    var desired = categoryOf(branch, 'q8', answers.q8);
    if (desired && tied.indexOf(desired) !== -1) {
      return { category: desired, scores: scores, highest: highest, tied: tied, resolvedBy: 'q8' };
    }

    var hypothesis = categoryOf(branch, 'q1', answers.q1);
    if (hypothesis && tied.indexOf(hypothesis) !== -1) {
      return { category: hypothesis, scores: scores, highest: highest, tied: tied, resolvedBy: 'q1' };
    }

    var fallback = null;
    for (var i = 0; i < FALLBACK_PRIORITY.length; i++) {
      if (tied.indexOf(FALLBACK_PRIORITY[i]) !== -1) {
        fallback = FALLBACK_PRIORITY[i];
        break;
      }
    }

    return { category: fallback, scores: scores, highest: highest, tied: tied, resolvedBy: 'fallback' };
  }

  /** Flattens quiz state into the personalization / analytics data model. */
  function buildProfile(state) {
    var branch = getBranch(state.branch);
    var answers = state.answers || {};
    var result = diagnose(answers, state.branch);

    var multiTags = function (questionId) {
      var selected = answers[questionId] || [];
      return selected
        .map(function (optionId) {
          return tagOf(branch, questionId, optionId);
        })
        .filter(Boolean);
    };

    return {
      branch: branch.id,
      diagnosis: result.category,
      resolvedBy: result.resolvedBy,
      scores: result.scores,
      hypothesis: categoryOf(branch, 'q1', answers.q1),
      desiredOutcome: categoryOf(branch, 'q8', answers.q8),
      need: tagOf(branch, 'q9', answers.q9),
      failedAttempts: multiTags('q6'),
      urgency: multiTags('q7'),
      answers: answers,
      attribution: state.attribution || {},
      timestamp: new Date().toISOString()
    };
  }

  var Core = {
    CATEGORIES: CATEGORIES,
    FALLBACK_PRIORITY: FALLBACK_PRIORITY,
    BRANCHES: BRANCHES,
    getBranch: getBranch,
    findQuestion: findQuestion,
    findOption: findOption,
    scoreObservations: scoreObservations,
    diagnose: diagnose,
    buildProfile: buildProfile
  };

  // Testable in Node without a DOM.
  if (typeof module !== 'undefined' && module.exports) module.exports = Core;
  if (typeof global !== 'undefined') global.KidslarQuiz = Core;

  if (typeof document === 'undefined') return;

  /* ========================================================================
   * 3. STATE — localStorage persistence + first-touch attribution
   * ====================================================================== */

  var STORAGE_KEY = 'kidslar_quiz_v1';
  var STATE_VERSION = 1;
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];

  function readAttributionFromUrl() {
    var attribution = {};
    try {
      var params = new URLSearchParams(global.location.search);
      UTM_KEYS.forEach(function (key) {
        var value = params.get(key);
        if (value) attribution[key] = value;
      });
    } catch (error) {
      /* older in-app browsers — attribution is optional, never fatal */
    }
    return attribution;
  }

  function freshState(branchId) {
    return {
      v: STATE_VERSION,
      branch: branchId,
      screen: 'landing',
      index: 0,
      resultStep: 0,
      answers: {},
      flags: { started: false, completed: false },
      attribution: readAttributionFromUrl(),
      startedAt: null,
      updatedAt: new Date().toISOString()
    };
  }

  function loadState(branchId) {
    var stored = null;
    try {
      stored = JSON.parse(global.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      stored = null;
    }

    if (!stored || stored.v !== STATE_VERSION || stored.branch !== branchId || typeof stored.answers !== 'object') {
      return freshState(branchId);
    }

    stored.answers = stored.answers || {};
    stored.flags = stored.flags || { started: false, completed: false };

    // First-touch wins; only fill keys we do not already have.
    var incoming = readAttributionFromUrl();
    stored.attribution = stored.attribution || {};
    Object.keys(incoming).forEach(function (key) {
      if (!stored.attribution[key]) stored.attribution[key] = incoming[key];
    });

    return stored;
  }

  function saveState(state) {
    state.updatedAt = new Date().toISOString();
    try {
      global.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      /* private mode / quota — quiz still works, just not resumable */
    }
  }

  function clearState() {
    try {
      global.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* no-op */
    }
  }

  /* ========================================================================
   * 4. ANALYTICS
   * ====================================================================== */

  function Analytics() {
    this.fired = {};
  }

  Analytics.prototype.emit = function (name, payload, onceKey) {
    if (onceKey) {
      if (this.fired[onceKey]) return;
      this.fired[onceKey] = true;
    }

    var detail = Object.assign({ event: name }, payload || {});

    try {
      document.dispatchEvent(new CustomEvent('kidslar:' + name, { detail: detail }));
    } catch (error) {
      /* no-op */
    }

    if (Array.isArray(global.dataLayer)) {
      global.dataLayer.push(detail);
    }

    // Custom events only — never a fabricated standard conversion event.
    if (typeof global.fbq === 'function') {
      try {
        global.fbq('trackCustom', toPixelName(name), payload || {});
      } catch (error) {
        /* no-op */
      }
    }
  };

  function toPixelName(name) {
    return name
      .split('_')
      .map(function (part) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join('');
  }

  /* ========================================================================
   * 5. CART
   * ====================================================================== */

  function cartRoutes() {
    return (global.routes && global.routes.cart_add_url) || '/cart/add';
  }

  function themeCart() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  /**
   * Adds to cart along the SAME path the live PDP's <product-form> uses, so
   * the theme's cart drawer (and the "Sıkça Birlikte Alınır" upsell rendered
   * inside it) behave identically here. Mirrors assets/product-form.js:
   *   - POST routes.cart_add_url (not a hand-built ".js" URL)
   *   - Accept: application/javascript, X-Requested-With, no Content-Type
   *   - sections + sections_url so the drawer re-renders server-side
   *   - setActiveElement so focus returns correctly when the drawer closes
   *   - publish('cart-update') so other theme components stay in sync
   * Deviating from this is what leaves the drawer/upsell half-wired.
   */
  function addToCart(variantId, properties, quantity) {
    var cart = themeCart();
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity || 1);

    if (properties) {
      Object.keys(properties).forEach(function (key) {
        if (properties[key]) formData.append('properties[' + key + ']', properties[key]);
      });
    }

    if (cart) {
      if (typeof cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cart.getSectionsToRender().map(function (section) {
            return section.id;
          })
        );
        formData.append('sections_url', global.location.pathname);
      }
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    return fetch(cartRoutes(), {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status) {
            // Shopify's own message is customer-facing and already localised;
            // anything else (network, parse) must fall back to the configured copy.
            var error = new Error(data.description || data.message || '');
            error.customerFacing = Boolean(data.description || data.message);
            if (typeof global.publish === 'function') {
              global.publish('cart-error', {
                source: 'kidslar-quiz',
                productVariantId: variantId,
                errors: data.errors || data.description,
                message: data.message
              });
            }
            throw error;
          }
          return data;
        });
      })
      .then(function (data) {
        /* Same event product-form.js publishes, so cart count bubbles and any
           other subscriber update rather than going stale. */
        if (typeof global.publish === 'function') {
          global.publish('cart-update', {
            source: 'kidslar-quiz',
            productVariantId: variantId,
            cartData: data
          });
        }

        /* renderContents re-renders the drawer sections returned above and
           opens it — this is what surfaces the live upsell, and it only runs
           after Shopify confirmed the add. */
        if (cart && typeof cart.renderContents === 'function') {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          global.location.href = (global.routes && global.routes.cart_url) || '/cart';
        }
        return data;
      });
  }

  /* ========================================================================
   * 6. RENDER HELPERS
   * ====================================================================== */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function splitLines(value) {
    if (!value) return [];
    return String(value)
      .split('\n')
      .map(function (line) {
        return line.trim();
      })
      .filter(Boolean);
  }

  /* --- notebook artwork -------------------------------------------------
   * One shape carries the whole page: the Turkish first-grade continuous loop
   * drill, drawn on four-line ruling. The hero shows a row losing the line.
   * The mechanism panels show the same loop freehand vs. run inside a groove.
   * Paths are static and authored, so innerHTML is safe here.
   */
  var ART = {
    heroDrill:
      'M 14 66 C 15.2 47.4, 19.8 34.6, 27.5 34 C 33.3 33.4, 31.5 46.2, 20.4 55.1 C 15.8 62.8, 33.7 66, 48 66 ' +
      'C 49.4 48.2, 54.6 35, 62.3 34.4 C 68.2 33.7, 66.1 46.9, 54.7 56.2 C 49.8 64.3, 67.7 67.7, 82 67.4 ' +
      'C 83.6 51.2, 89.4 38.9, 96.8 38.3 C 102.5 37.7, 100.1 50, 88.8 58.6 C 83.7 66.5, 101.7 69.8, 116 69.1 ' +
      'C 118 51.5, 124.5 38.1, 132.1 37.4 C 138 36.7, 135.1 50.1, 123.2 59.5 C 117.8 68.3, 135.7 72, 150 70.9 ' +
      'C 152.3 50.3, 159.9 34.8, 167.9 34.1 C 174.1 33.3, 170.8 48.8, 157.9 59.7 C 151.9 70, 169.7 74.4, 184 72.8 ' +
      'C 186.5 57.1, 194.3 44.9, 201.5 44.3 C 207.1 43.7, 203.5 55.9, 191.5 64.4 C 185.7 73.1, 203.7 76.9, 218 74.8 ' +
      'C 220.9 59, 229.4 46.6, 236.6 46 C 242.2 45.4, 238.1 57.7, 225.9 66.4 C 219.7 75.5, 237.7 79.4, 252 76.9 ' +
      'C 255.4 52.8, 265.5 34.7, 274 33.8 C 280.5 32.9, 275.7 51, 261.3 63.6 C 254 76.5, 271.7 82, 286 79',
    loopFree:
      'M 16 66 C 17.5 47.4, 23.7 34.6, 33.8 34 C 41.5 33.4, 39.2 46.2, 24.5 55.1 C 18.3 62.8, 42.1 66, 61 66 ' +
      'C 64.7 64, 75.5 53, 84.5 52.4 C 91.5 51.8, 86.4 62.9, 70.9 70.7 C 63.1 79.5, 87.1 83.3, 106 80.1 ' +
      'C 112.5 77, 129.9 63.1, 139.2 62.4 C 146.4 61.7, 137.5 75.6, 118.9 85.4 C 108.1 98.5, 132.1 104.4, 151 97.2',
    loopGuided:
      'M 18 74 C 19.5 54.3, 25.6 40.7, 35.4 40 C 43 39.3, 40.7 52.9, 26.3 62.4 C 20.3 70.6, 43.5 74, 62 74 ' +
      'C 63.5 54.3, 69.6 40.7, 79.4 40 C 87 39.3, 84.7 52.9, 70.3 62.4 C 64.3 70.6, 87.5 74, 106 74 ' +
      'C 107.5 54.3, 113.6 40.7, 123.4 40 C 131 39.3, 128.7 52.9, 114.3 62.4 C 108.3 70.6, 131.5 74, 150 74'
  };

  /** The hero: a drill row that starts controlled and slides off the line. */
  function heroArt(label) {
    return (
      '<svg class="kq-hero__art" viewBox="0 0 300 100" role="img" aria-label="' +
      String(label || '').replace(/"/g, '&quot;') +
      '">' +
      '<g class="kq-rules" fill="none">' +
      '<line x1="0" y1="34" x2="300" y2="34"/>' +
      '<line class="kq-rules__mid" x1="0" y1="50" x2="300" y2="50"/>' +
      '<line class="kq-rules__base" x1="0" y1="66" x2="300" y2="66"/>' +
      '<line x1="0" y1="86" x2="300" y2="86"/>' +
      '</g>' +
      '<path class="kq-stroke" d="' + ART.heroDrill + '"/>' +
      '</svg>'
    );
  }

  /** Mechanism panel art. Guided adds the groove channel and a start point. */
  function loopArt(guided) {
    return (
      '<svg class="kq-mechanism__art" viewBox="0 0 168 112" aria-hidden="true">' +
      '<g class="kq-rules" fill="none">' +
      '<line x1="8" y1="40" x2="160" y2="40"/>' +
      '<line class="kq-rules__base" x1="8" y1="74" x2="160" y2="74"/>' +
      '</g>' +
      (guided ? '<path class="kq-mechanism__channel" d="' + ART.loopGuided + '"/>' : '') +
      '<path class="kq-stroke" d="' + (guided ? ART.loopGuided : ART.loopFree) + '"/>' +
      (guided ? '<circle class="kq-mechanism__start" cx="18" cy="74" r="3.6"/>' : '') +
      '</svg>'
    );
  }

  function withAttribution(url, attribution) {
    if (!url) return url;
    var keys = Object.keys(attribution || {});
    if (!keys.length) return url;

    var separator = url.indexOf('?') === -1 ? '?' : '&';
    var query = keys
      .map(function (key) {
        return encodeURIComponent(key) + '=' + encodeURIComponent(attribution[key]);
      })
      .join('&');
    return url + separator + query;
  }

  /* ========================================================================
   * 7. CONTROLLER
   * ====================================================================== */

  function QuizApp(root) {
    this.root = root;
    this.stage = root.querySelector('[data-kq-stage]');
    this.progress = root.querySelector('[data-kq-progress]');
    this.progressBar = root.querySelector('[data-kq-progress-bar]');
    this.progressLabel = root.querySelector('[data-kq-progress-label]');
    this.backButton = root.querySelector('[data-kq-back]');
    this.topbar = root.querySelector('[data-kq-topbar]');
    this.liveRegion = root.querySelector('[data-kq-live]');

    this.config = this.readConfig();
    this.branch = getBranch(this.config.branch);
    this.questions = this.branch.questions;
    this.analytics = new Analytics();
    this.state = loadState(this.branch.id);
    this.locked = false;
    this.timers = [];
    this.offerObservers = [];
    this.offerListeners = [];
    this.stickyBar = null;
    this.prefersReducedMotion =
      global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onBack = this.goBack.bind(this);
    if (this.backButton) this.backButton.addEventListener('click', this.onBack);

    this.root.addEventListener('click', this.onRootClick.bind(this));

    this.analytics.emit('quiz_view', this.basePayload(), 'quiz_view');
    this.restore();
  }

  QuizApp.prototype.readConfig = function () {
    var node = this.root.querySelector('[data-kq-config]');
    if (!node) return { branch: 'ilkokul' };
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[kidslar-quiz] config parse failed', error);
      return { branch: 'ilkokul' };
    }
  };

  QuizApp.prototype.basePayload = function () {
    return Object.assign({ quiz_branch: this.branch.id }, this.state.attribution || {});
  };

  QuizApp.prototype.destroy = function () {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.teardownOffer();
    if (this.backButton) this.backButton.removeEventListener('click', this.onBack);
  };

  QuizApp.prototype.later = function (fn, delay) {
    var timer = setTimeout(fn, this.prefersReducedMotion ? Math.min(delay, 120) : delay);
    this.timers.push(timer);
    return timer;
  };

  /* ---------- navigation ---------- */

  QuizApp.prototype.restore = function () {
    if (this.state.screen === 'result' && this.isComplete()) {
      this.renderResult();
      return;
    }
    if (this.state.screen === 'question' && this.state.flags.started) {
      this.renderQuestion(Math.min(this.state.index, this.questions.length - 1));
      return;
    }
    this.renderLanding();
  };

  QuizApp.prototype.isComplete = function () {
    var self = this;
    return this.questions.every(function (question) {
      var value = self.state.answers[question.id];
      return question.type === 'multi' ? Array.isArray(value) && value.length > 0 : Boolean(value);
    });
  };

  QuizApp.prototype.start = function () {
    if (!this.state.flags.started) {
      this.state.flags.started = true;
      this.state.startedAt = new Date().toISOString();
    }
    this.analytics.emit('quiz_start', this.basePayload(), 'quiz_start');
    this.renderQuestion(0);
  };

  QuizApp.prototype.goBack = function () {
    if (this.state.screen === 'result') {
      var flow = (this.profile && RESULT_FLOWS[this.profile.diagnosis]) || null;
      if (flow && this.state.resultStep > 0) {
        this.renderResultStep(this.state.resultStep - 1);
        return;
      }
      this.renderQuestion(this.questions.length - 1);
      return;
    }
    if (this.state.index <= 0) {
      this.renderLanding();
      return;
    }
    this.renderQuestion(this.state.index - 1);
  };

  QuizApp.prototype.restart = function () {
    this.state = freshState(this.branch.id);
    saveState(this.state);
    this.profile = null;
    this.analytics.fired = {};
    this.renderLanding();
  };

  /* ---------- shared chrome ---------- */

  QuizApp.prototype.setProgress = function (index) {
    if (!this.progress) return;

    if (index == null) {
      this.progress.hidden = true;
      this.syncTopbar();
      return;
    }

    var total = this.questions.length;
    var current = index + 1;
    this.progress.hidden = false;
    if (this.progressLabel) this.progressLabel.textContent = current + ' / ' + total;
    if (this.progressBar) this.progressBar.style.width = (current / total) * 100 + '%';
    this.progress.setAttribute('aria-valuenow', String(current));
    this.progress.setAttribute('aria-valuemax', String(total));
    this.syncTopbar();
  };

  /** Collapse the chrome row when neither control is showing. */
  QuizApp.prototype.syncTopbar = function () {
    if (!this.topbar) return;
    var backHidden = !this.backButton || this.backButton.hidden;
    var progressHidden = !this.progress || this.progress.hidden;
    this.topbar.hidden = backHidden && progressHidden;
  };

  QuizApp.prototype.setBackVisible = function (visible) {
    if (this.backButton) this.backButton.hidden = !visible;
    this.syncTopbar();
  };

  QuizApp.prototype.swap = function (node, options) {
    var opts = options || {};
    this.stage.querySelectorAll('.kq-video__el').forEach(function (video) {
      video.pause();
    });
    this.stage.innerHTML = '';
    node.classList.add('kq-enter');
    this.stage.appendChild(node);

    // force reflow so the enter transition always plays
    void node.offsetWidth;
    node.classList.remove('kq-enter');

    if (opts.announce && this.liveRegion) this.liveRegion.textContent = opts.announce;
    if (opts.scroll !== false) this.scrollToTop();
    if (opts.focus !== false) {
      var target = node.querySelector('[data-kq-focus]') || node;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    }
  };

  QuizApp.prototype.scrollToTop = function () {
    var rect = this.root.getBoundingClientRect();
    var target = rect.top + global.pageYOffset - 12;
    if (Math.abs(global.pageYOffset - target) < 8) return;
    try {
      global.scrollTo({ top: Math.max(target, 0), behavior: this.prefersReducedMotion ? 'auto' : 'smooth' });
    } catch (error) {
      global.scrollTo(0, Math.max(target, 0));
    }
  };

  /* ---------- screens ---------- */

  QuizApp.prototype.renderLanding = function () {
    this.state.screen = 'landing';
    this.state.index = 0;
    saveState(this.state);

    this.setProgress(null);
    this.setBackVisible(false);

    var copy = this.config.landing || {};
    var wrap = el('div', 'kq-screen kq-landing');

    var art = document.createElement('div');
    art.innerHTML = heroArt(copy.artLabel || copy.title || '');
    if (art.firstChild) wrap.appendChild(art.firstChild);

    if (copy.eyebrow) wrap.appendChild(el('p', 'kq-eyebrow', copy.eyebrow));
    if (copy.title) wrap.appendChild(el('h1', 'kq-landing__title', copy.title));
    if (copy.subtitle) wrap.appendChild(el('p', 'kq-landing__subtitle', copy.subtitle));

    var cta = el('button', 'kq-button kq-button--primary kq-landing__cta', copy.cta || 'Teste Başla');
    cta.type = 'button';
    cta.setAttribute('data-kq-action', 'start');
    cta.setAttribute('data-kq-focus', '');
    wrap.appendChild(cta);

    if (copy.footnote) wrap.appendChild(el('p', 'kq-landing__footnote', copy.footnote));

    if (this.state.flags.started && !this.isComplete()) {
      var resume = el('button', 'kq-textlink', copy.resume || 'Kaldığım yerden devam et');
      resume.type = 'button';
      resume.setAttribute('data-kq-action', 'resume');
      wrap.appendChild(resume);
    }

    this.swap(wrap, { focus: false, scroll: false });
  };

  QuizApp.prototype.renderQuestion = function (index) {
    var question = this.questions[index];
    if (!question) return;

    this.state.screen = 'question';
    this.state.index = index;
    this.profile = null;
    saveState(this.state);

    this.setProgress(index);
    this.setBackVisible(true);
    this.locked = false;

    var wrap = el('div', 'kq-screen kq-question');
    wrap.setAttribute('data-kq-question', question.id);

    var header = el('div', 'kq-question__header');
    var title = el('h2', 'kq-question__title', question.title);
    title.id = 'kq-title-' + question.id;
    title.setAttribute('data-kq-focus', '');
    title.setAttribute('tabindex', '-1');
    header.appendChild(title);
    if (question.helper) header.appendChild(el('p', 'kq-question__helper', question.helper));
    wrap.appendChild(header);

    var group = el('div', 'kq-options');
    group.setAttribute('role', question.type === 'multi' ? 'group' : 'radiogroup');
    group.setAttribute('aria-labelledby', title.id);

    var selected = this.state.answers[question.id];

    question.options.forEach(function (option) {
      var isSelected =
        question.type === 'multi'
          ? Array.isArray(selected) && selected.indexOf(option.id) !== -1
          : selected === option.id;

      var card = el('button', 'kq-option');
      card.type = 'button';
      card.setAttribute('data-kq-action', 'answer');
      card.setAttribute('data-kq-option', option.id);
      card.setAttribute('role', question.type === 'multi' ? 'checkbox' : 'radio');
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      if (isSelected) card.classList.add('is-selected');

      var mark = el('span', 'kq-option__mark');
      mark.setAttribute('aria-hidden', 'true');
      card.appendChild(mark);
      card.appendChild(el('span', 'kq-option__text', option.text));
      group.appendChild(card);
    });

    wrap.appendChild(group);

    if (question.type === 'multi') {
      var footer = el('div', 'kq-question__footer');
      var next = el('button', 'kq-button kq-button--primary', this.config.copy.continueLabel || 'Devam et');
      next.type = 'button';
      next.setAttribute('data-kq-action', 'next');
      var hasSelection = Array.isArray(selected) && selected.length > 0;
      next.disabled = !hasSelection;
      footer.appendChild(next);
      wrap.appendChild(footer);
    }

    this.swap(wrap, { announce: question.title });

    this.analytics.emit(
      'quiz_question_view',
      Object.assign(this.basePayload(), {
        question_number: index + 1,
        question_id: question.id,
        question_type: question.type
      }),
      'view_' + question.id
    );
  };

  QuizApp.prototype.renderLoading = function () {
    this.state.screen = 'loading';
    saveState(this.state);

    this.setProgress(this.questions.length - 1);
    this.setBackVisible(false);

    var copy = this.config.copy || {};
    var wrap = el('div', 'kq-screen kq-loading');
    wrap.appendChild(el('p', 'kq-loading__text', copy.loadingText || 'Cevaplarınızı analiz ediyoruz...'));

    var track = el('div', 'kq-loading__track');
    track.setAttribute('role', 'progressbar');
    track.setAttribute('aria-label', copy.loadingText || 'Cevaplarınızı analiz ediyoruz...');
    var fill = el('span', 'kq-loading__fill');
    fill.style.animationDuration = (this.config.loadingDuration || 1400) + 'ms';
    track.appendChild(fill);
    wrap.appendChild(track);

    this.swap(wrap, { announce: copy.loadingText, focus: false });
    var self = this;
    this.later(function () {
      self.renderResult();
    }, this.config.loadingDuration || 1400);
  };

  /**
   * Entry point for the result. The diagnosis is calculated once here and then
   * cached on the instance, so stepping between result screens never
   * recalculates it.
   */
  QuizApp.prototype.renderResult = function () {
    if (!this.profile) this.profile = buildProfile(this.state);

    this.state.screen = 'result';
    this.state.flags.completed = true;
    saveState(this.state);

    var payload = this.resultPayload();
    this.analytics.emit('quiz_complete', payload, 'quiz_complete');
    this.analytics.emit('quiz_result_view', payload, 'quiz_result_view_' + this.profile.diagnosis);

    var flow = RESULT_FLOWS[this.profile.diagnosis];
    if (flow && flow.length) {
      this.renderResultStep(this.state.resultStep || 0);
      return;
    }
    this.renderResultPage();
  };

  /** One screen of a staged result flow. */
  /**
   * `opts` is only for RE-renders of the step already on screen (picking a
   * different package). Moving to a NEW step still scrolls and focuses, which
   * is what `swap` does by default — pass nothing to get that.
   *   opts.scroll === false  -> leave the reader where they are
   *   opts.focus  === false  -> do not yank focus to the top of the step
   */
  QuizApp.prototype.renderResultStep = function (index, opts) {
    opts = opts || {};
    var flow = RESULT_FLOWS[this.profile.diagnosis] || [];
    var step = flow[Math.min(Math.max(index, 0), flow.length - 1)];
    if (!step) return;

    /* Every step render replaces the screen, so anything the offer screen
       attached (sticky bar, observers, listeners) must go with it. */
    this.teardownOffer();

    this.state.resultStep = flow.indexOf(step);
    saveState(this.state);

    this.setProgress(null);
    this.setBackVisible(this.state.resultStep > 0);

    var wrap = el('div', 'kq-screen kq-result kq-step');
    var announce = '';

    /* Section marker, shared by the screens that carry one. */
    if (step.eyebrow) {
      var eyebrow = el('h2', 'kq-step__eyebrow');
      eyebrow.setAttribute('data-kq-focus', '');
      eyebrow.setAttribute('tabindex', '-1');
      eyebrow.textContent = step.eyebrow;
      wrap.appendChild(eyebrow);
      announce = step.eyebrow;
    }

    if (step.type === 'statement') {
      var head = el('header', 'kq-result__head');
      var headline = el('h1', 'kq-result__title');
      headline.setAttribute('data-kq-focus', '');
      headline.setAttribute('tabindex', '-1');
      headline.textContent = step.headline;
      head.appendChild(headline);
      if (step.support) head.appendChild(el('p', 'kq-result__sub', step.support));
      wrap.appendChild(head);
      announce = step.headline;

      if (step.problem) {
        var box = el('section', 'kq-card kq-recognition');
        if (step.label) box.appendChild(el('p', 'kq-outcome__label', step.label));
        var list = el('ul', 'kq-recognition__list');
        list.appendChild(el('li', 'kq-recognition__item', step.problem));
        box.appendChild(list);
        wrap.appendChild(box);
      }

      if (step.realization) {
        var realization = el('section', 'kq-card kq-explain');
        realization.appendChild(el('p', null, step.realization));
        wrap.appendChild(realization);
      }
    }

    if (step.type === 'video') {
      var media = this.buildStepVideo(step);
      if (media) wrap.appendChild(media);

      /* Explanation reads below the media, as its own section. */
      if (step.headline || (step.paragraphs && step.paragraphs.length)) {
        var explain = el('section', 'kq-card kq-explain kq-explain--lead');
        if (step.headline) explain.appendChild(el('h2', 'kq-h2', step.headline));
        (step.paragraphs || []).forEach(function (paragraph) {
          explain.appendChild(el('p', null, paragraph));
        });
        wrap.appendChild(explain);
      }
    }

    if (step.type === 'recommendation') {
      if (step.headline) {
        var recHead = el('h1', 'kq-result__title kq-step__headline');
        recHead.textContent = step.headline;
        wrap.appendChild(recHead);
      }
      if (step.bridge) {
        var bridge = el('section', 'kq-card kq-explain kq-explain--lead');
        (Array.isArray(step.bridge) ? step.bridge : [step.bridge]).forEach(function (paragraph) {
          bridge.appendChild(el('p', null, paragraph));
        });
        wrap.appendChild(bridge);
      }
      var compare = this.buildCompare(step);
      if (compare) wrap.appendChild(compare);
      var experts = this.buildExperts(step);
      if (experts) wrap.appendChild(experts);
      var need = this.buildNeedSection(step);
      if (need) wrap.appendChild(need);
    }

    if (step.type === 'content') {
      if (step.headline) {
        var contentHead = el('h1', 'kq-result__title kq-step__headline');
        contentHead.textContent = step.headline;
        wrap.appendChild(contentHead);
      }
      var body = this.buildStepBlocks(step.blocks);
      if (body) wrap.appendChild(body);
    }

    if (step.type === 'offer') {
      var offer = this.buildOffer(step);
      if (offer) wrap.appendChild(offer);
    }

    if (step.type !== 'offer' && step.cta && step.ctaType === 'pdp') {
      var product = this.config.product || {};
      var shopActions = el('div', 'kq-step__actions');
      if (product.url) {
        var pdp = el('a', 'kq-button kq-button--primary');
        pdp.href = withAttribution(product.url, this.state.attribution);
        pdp.setAttribute('data-kq-action', 'pdp');
        pdp.appendChild(el('span', null, step.cta));
        shopActions.appendChild(pdp);
      } else {
        shopActions.appendChild(
          el('p', 'kq-error', 'Ürün bağlantısı ayarlanmamış. Tema düzenleyicisinden ürünü seçin.')
        );
      }
      wrap.appendChild(shopActions);
    } else if (step.type !== 'offer' && step.cta) {
      var actions = el('div', 'kq-step__actions');
      var next = el('button', 'kq-button kq-button--primary');
      next.type = 'button';
      next.setAttribute('data-kq-action', 'result-next');
      next.appendChild(el('span', null, step.cta));
      if (step.ctaArrow) {
        var arrow = el('span', 'kq-button__arrow', step.ctaArrow);
        arrow.setAttribute('aria-hidden', 'true');
        next.appendChild(arrow);
      }
      actions.appendChild(next);
      wrap.appendChild(actions);
    }

    /* The offer screen is a commerce layout, not a reading column — it gets
       a wider container on desktop so media and buy box can sit side by side. */
    this.root.classList.toggle('kq-has-offer', step.type === 'offer');

    this.swap(wrap, { announce: announce, scroll: opts.scroll, focus: opts.focus });
    this.playStepVideo();

    if (step.type === 'offer') {
      this.mountStickyCta(step, this.config.product || {});
      this.watchOfferEngagement();
      this.analytics.emit('quiz_offer_view', this.offerPayload(), 'offer_view');
    }

    this.analytics.emit(
      'quiz_result_step',
      Object.assign(this.basePayload(), {
        diagnosis: this.profile.diagnosis,
        need: this.profile.need,
        step_index: this.state.resultStep + 1,
        step_id: step.id
      }),
      'result_step_' + this.profile.diagnosis + '_' + step.id
    );
  };

  /**
   * Expert testimonials in a snap carousel: one card fills the viewport with
   * the next peeking, so it reads as swipeable without any JS.
   */
  /**
   * Shared shell for a horizontal, swipeable proof carousel: heading, a
   * scroll-snap track, and (once cards exist) dot pagination plus prev/next
   * buttons. Nav chrome is skipped entirely for 0-1 cards — nothing to page
   * through. Active-dot tracking uses IntersectionObserver against the track
   * itself, so it stays correct however the user gets there: swipe, arrow
   * click, or dot click.
   */
  function buildCarouselShell(heading, ariaLabel) {
    var section = el('section', 'kq-proof');
    section.appendChild(el('h2', 'kq-h2', heading));
    var wrap = el('div', 'kq-carousel-wrap');
    var track = el('div', 'kq-carousel');
    track.setAttribute('tabindex', '0');
    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', ariaLabel || heading);
    wrap.appendChild(track);
    section.appendChild(wrap);
    return { section: section, wrap: wrap, track: track };
  }

  function attachCarouselNav(wrap, track) {
    var count = track.children.length;
    if (count < 2) return;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var goTo = function (index) {
      var card = track.children[Math.max(0, Math.min(count - 1, index))];
      if (card) card.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
    };

    var dots = el('div', 'kq-carousel-dots');
    dots.setAttribute('role', 'tablist');
    var dotEls = [];
    for (var i = 0; i < count; i++) {
      (function (index) {
        var dot = el('button', 'kq-carousel-dot');
        dot.type = 'button';
        dot.setAttribute('aria-label', index + 1 + ' / ' + count);
        dot.addEventListener('click', function () {
          goTo(index);
        });
        dots.appendChild(dot);
        dotEls.push(dot);
      })(i);
    }
    wrap.appendChild(dots);

    var prev = el('button', 'kq-carousel-nav kq-carousel-nav--prev');
    prev.type = 'button';
    prev.setAttribute('aria-label', 'Önceki');
    prev.appendChild(el('span', null, '‹'));
    var next = el('button', 'kq-carousel-nav kq-carousel-nav--next');
    next.type = 'button';
    next.setAttribute('aria-label', 'Sonraki');
    next.appendChild(el('span', null, '›'));
    wrap.appendChild(prev);
    wrap.appendChild(next);

    var activeIndex = 0;
    var setActive = function (index) {
      activeIndex = index;
      dotEls.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      prev.disabled = index === 0;
      next.disabled = index === count - 1;
    };
    setActive(0);

    prev.addEventListener('click', function () {
      goTo(activeIndex - 1);
    });
    next.addEventListener('click', function () {
      goTo(activeIndex + 1);
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
              var index = Array.prototype.indexOf.call(track.children, entry.target);
              if (index > -1) setActive(index);
            }
          });
        },
        { root: track, threshold: [0.6] }
      );
      Array.prototype.forEach.call(track.children, function (card) {
        observer.observe(card);
      });
    }
  }

  QuizApp.prototype.buildExperts = function (step) {
    if (!step.experts || !step.experts.length) return null;
    var images = this.config.expertImages || {};
    var shell = buildCarouselShell(step.expertsHeading);

    step.experts.forEach(function (expert) {
      var card = el('figure', 'kq-expert');
      if (expert.tag) card.appendChild(el('p', 'kq-proof-tag', expert.tag));
      card.appendChild(el('blockquote', 'kq-expert__quote', '“' + expert.quote + '”'));
      card.appendChild(el('hr', 'kq-expert__divider'));

      var who = el('figcaption', 'kq-expert__who');
      var src = expert.imageKey ? images[expert.imageKey] : null;
      if (src) {
        var img = document.createElement('img');
        img.className = 'kq-expert__photo';
        img.src = src;
        img.alt = expert.name;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.width = 52;
        img.height = 52;
        who.appendChild(img);
      }
      var meta = el('div', 'kq-expert__meta');
      meta.appendChild(el('p', 'kq-expert__name', expert.name));
      meta.appendChild(el('p', 'kq-expert__title', expert.title));
      who.appendChild(meta);
      card.appendChild(who);
      shell.track.appendChild(card);
    });

    attachCarouselNav(shell.wrap, shell.track);
    return shell.section;
  };

  /** Personalises the closing argument from the parent's own Q9 answer. */
  QuizApp.prototype.buildNeedSection = function (step) {
    var answered = findOption(this.branch, 'q9', this.state.answers.q9);
    var need = this.profile.need;
    var body = (step.needCopy || {})[need];
    var guarantee = this.config.guarantee || {};
    var useGuarantee = need === 'risk_reversal' && guarantee.enabled && guarantee.text;

    if (!answered && !body && !useGuarantee) return null;

    var section = el('section', 'kq-card kq-explain kq-explain--lead kq-need-close');
    if (answered) {
      section.appendChild(el('p', 'kq-outcome__label', 'Sizin ihtiyacınız'));
      var quote = document.createElement('blockquote');
      quote.className = 'kq-pullquote';
      quote.appendChild(el('p', null, answered.text));
      section.appendChild(quote);
    }
    splitLines(body).forEach(function (line) {
      section.appendChild(el('p', null, line));
    });

    if (useGuarantee) {
      if (guarantee.heading) section.appendChild(el('h3', 'kq-h3', guarantee.heading));
      splitLines(guarantee.text).forEach(function (line) {
        section.appendChild(el('p', null, line));
      });
      if (guarantee.linkUrl && guarantee.linkLabel) {
        var link = el('a', 'kq-textlink', guarantee.linkLabel);
        link.href = guarantee.linkUrl;
        section.appendChild(link);
      }
    }
    return section;
  };

  /** WhatsApp glyph, inline so the button needs no network request. */
  function whatsappIcon() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'kq-wa-icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('fill', 'currentColor');
    path.setAttribute(
      'd',
      'M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.19 8.19 0 0 1-1.26-4.36c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.19 8.19 0 0 1 2.41 5.83c0 4.54-3.7 8.21-8.24 8.21Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.24-.64.8-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.15.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42-.14-.01-.31-.01-.47-.01-.17 0-.43.06-.66.31-.22.25-.87.85-.87 2.07 0 1.22.89 2.4 1.02 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.47-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.17-.48-.29Z'
    );
    svg.appendChild(path);
    return svg;
  }

  function buildStars(count) {
    if (!count) return null;
    var wrap = el('span', 'kq-stars');
    wrap.setAttribute('aria-label', count + ' üzerinden 5 yıldız');
    for (var i = 1; i <= 5; i++) {
      wrap.appendChild(el('span', i <= count ? 'kq-stars__star is-filled' : 'kq-stars__star', '★'));
    }
    return wrap;
  }

  /**
   * Discount percentage recomputed from the live Shopify amounts. Returns
   * null unless compare-at is genuinely higher than price — a claimed
   * campaign badge is never rendered on faith.
   */
  function discountPercent(product) {
    var price = Number(product.priceAmount);
    var compare = Number(product.compareAtAmount);
    if (!isFinite(price) || !isFinite(compare) || compare <= price || price <= 0) return null;
    return Math.round(((compare - price) / compare) * 100);
  }

  /** 89999 -> "899,99 TL". Matches the storefront's tr-TR money format. */
  function money(amount) {
    var n = Number(amount);
    if (!isFinite(n)) return '';
    var whole = Math.floor(n / 100);
    var cents = String(n % 100);
    if (cents.length < 2) cents = '0' + cents;
    return String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + cents + ' TL';
  }

  /* ========================================================================
   * OFFER SCREEN
   * Every price, rating, stock and content value here is read from
   * `config.product` (live Shopify objects) or from copy verified against
   * the live PDP / store policy pages. Nothing is invented at render time.
   * ====================================================================== */

  QuizApp.prototype.buildOffer = function (step) {
    var product = this.config.product || {};
    var section = el('section', 'kq-offer');

    section.appendChild(this.buildOfferHero(step, product));

    var contents = this.buildOfferContents(step);
    if (contents) section.appendChild(contents);

    var guarantee = this.buildOfferGuarantee(step);
    if (guarantee) section.appendChild(guarantee);

    var reviews = this.buildOfferReviews(step);
    if (reviews) section.appendChild(reviews);

    var recap = this.buildOfferRecap(step, product);
    if (recap) section.appendChild(recap);

    var faq = this.buildOfferFaq(step);
    if (faq) section.appendChild(faq);

    return section;
  };

  /** Price block: campaign badge, current price, compare-at. Shared by hero + recap. */
  QuizApp.prototype.buildOfferPrice = function (step, product, variant) {
    var wrap = el('div', 'kq-offer-price' + (variant ? ' kq-offer-price--' + variant : ''));
    var percent = discountPercent(product);

    if (percent && step.campaignLabel) {
      /* The label only survives if the live pricing actually produces the
         percentage it claims (1pt tolerance for rounding). */
      var claimed = (step.campaignLabel.match(/\d+/) || [])[0];
      if (claimed && Math.abs(Number(claimed) - percent) <= 1) {
        wrap.appendChild(el('span', 'kq-offer-price__campaign', step.campaignLabel));
      }
    }

    var row = el('div', 'kq-offer-price__row');
    if (product.price) row.appendChild(el('span', 'kq-offer-price__now', product.price));
    if (product.compareAtPrice) {
      row.appendChild(el('span', 'kq-offer-price__was', product.compareAtPrice));
    }
    if (percent) row.appendChild(el('span', 'kq-offer-price__save', '%' + percent + ' indirim'));
    wrap.appendChild(row);

    if (step.shippingNote) wrap.appendChild(el('p', 'kq-offer-price__note', step.shippingNote));
    return wrap;
  };

  /** Add-to-cart button + live status line. Uses the theme's native cart. */
  QuizApp.prototype.buildOfferCta = function (step, product, context, pkg) {
    var wrap = el('div', 'kq-offer-cta');

    if (product.variantId && product.available) {
      var button = el('button', 'kq-button kq-button--primary kq-offer-cta__button');
      button.type = 'button';
      button.setAttribute('data-kq-action', 'atc-offer');
      button.setAttribute('data-kq-context', context || 'hero');
      button.appendChild(el('span', 'kq-offer-cta__label', step.cta || '21 Günlük Gelişime Başla'));
      if (pkg) button.appendChild(el('span', 'kq-offer-cta__price', money(pkg.amount)));
      wrap.appendChild(button);
    } else if (product.url) {
      /* Out of stock: never fake availability — send to the PDP instead. */
      var link = el('a', 'kq-button kq-button--secondary kq-offer-cta__button');
      link.href = withAttribution(product.url, this.state.attribution);
      link.setAttribute('data-kq-action', 'pdp');
      link.appendChild(el('span', null, 'Ürün sayfasına git'));
      wrap.appendChild(el('p', 'kq-offer-cta__soldout', 'Bu ürün şu anda stokta görünmüyor.'));
      wrap.appendChild(link);
    }

    var status = el('p', 'kq-cta__status');
    status.setAttribute('data-kq-cart-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    wrap.appendChild(status);
    return wrap;
  };

  /* --- SECTION 1: commerce hero (media + buy box) ------------------------- */

  QuizApp.prototype.selectedPackage = function (step) {
    var packages = step.packages || [];
    if (!packages.length) return null;
    var self = this;
    var found = packages.filter(function (p) {
      return p.id === self.offerPackageId;
    })[0];
    if (found) return found;
    return packages.filter(function (p) { return p.recommended; })[0] || packages[0];
  };

  QuizApp.prototype.buildOfferHero = function (step, product) {
    var hero = el('section', 'kq-offer-hero');

    /* Two lines above the product photo and nothing else: the promise, then
       one tight identification row. Everything that used to sit here (a
       second eyebrow, a standalone product-name block, a separate rating row)
       either moved into that row or moved below the photo, because on a phone
       each of them was pushing the package selector off the first screen. */
    var head = el('div', 'kq-offer-hero__head');
    if (step.headline) {
      var headline = el('h1', 'kq-offer-hero__headline');
      headline.textContent = step.headline;
      head.appendChild(headline);
    }
    var idLine = el('div', 'kq-offer-hero__id');
    if (step.productName) idLine.appendChild(el('p', 'kq-offer-hero__product', step.productName));
    var rating = this.buildOfferRating(step, product);
    if (rating) idLine.appendChild(rating);
    if (idLine.childNodes.length) head.appendChild(idLine);
    if (step.systemLabel) head.appendChild(el('p', 'kq-offer-hero__system', step.systemLabel));
    hero.appendChild(head);

    /* Two columns on desktop (media | buy box), stacked on mobile with the
       buy box immediately under the media — the commerce controls must not
       be pushed below the fold. */
    var grid = el('div', 'kq-offer-hero__grid');
    var media = el('div', 'kq-offer-hero__media');
    var gallery = this.buildOfferGallery(product);
    if (gallery) media.appendChild(gallery);
    grid.appendChild(media);
    grid.appendChild(this.buildBuyBox(step, product, 'hero'));
    hero.appendChild(grid);
    return hero;
  };

  /**
   * The buy box — the page's primary object. Reading order is the order a
   * parent decides in: how many sets, what it costs, what the campaign takes
   * off, what comes free, what protects them, then the button.
   *
   * Two contexts share this module. 'hero' is the full decision surface.
   * 'recap' is a lighter close at the bottom of the page: it shows the
   * choice already made instead of asking for it again, so the bottom of the
   * page is not a second decision wall.
   *
   * Prices come from the verified `packages` table; nothing is computed from
   * a guessed discount rate.
   */
  QuizApp.prototype.buildBuyBox = function (step, product, context) {
    var box = el('div', 'kq-buybox kq-buybox--' + context);
    box.setAttribute('data-kq-buybox', context);

    var pkg = this.selectedPackage(step);
    var packages = step.packages || [];
    var compact = context === 'recap';

    /* 1. Package choice. */
    if (!compact && packages.length > 1) {
      if (step.packagesEyebrow) {
        box.appendChild(el('p', 'kq-buybox__eyebrow', step.packagesEyebrow));
      }
      if (step.packagesHeading) {
        box.appendChild(el('p', 'kq-buybox__label kq-buybox__heading', step.packagesHeading));
      }
      var list = el('div', 'kq-packages');
      list.setAttribute('role', 'radiogroup');
      list.setAttribute('aria-label', step.packagesHeading || 'Paket seçimi');

      packages.forEach(function (item) {
        var isSel = pkg && item.id === pkg.id;
        var card = el('button', 'kq-package' + (isSel ? ' is-selected' : '') + (item.recommended ? ' is-recommended' : ''));
        card.type = 'button';
        card.setAttribute('role', 'radio');
        card.setAttribute('aria-checked', isSel ? 'true' : 'false');
        card.setAttribute('data-kq-action', 'select-package');
        card.setAttribute('data-package-id', item.id);

        if (item.ribbon) card.appendChild(el('span', 'kq-package__ribbon', item.ribbon));
        var main = el('span', 'kq-package__main');
        var radio = el('span', 'kq-package__radio');
        radio.setAttribute('aria-hidden', 'true');
        main.appendChild(radio);

        var text = el('span', 'kq-package__text');
        if (item.eyebrow) text.appendChild(el('span', 'kq-package__eyebrow', item.eyebrow));
        text.appendChild(el('span', 'kq-package__label', item.label));
        text.appendChild(el('span', 'kq-package__sub', item.qty + ' set'));
        main.appendChild(text);

        /* Price only, plus the per-set figure where it differs from the
           headline number. The compare-at and the saving live in the single
           price block below — repeating them here made the same discount
           appear three times inside one card. */
        var money_ = el('span', 'kq-package__money');
        money_.appendChild(el('span', 'kq-package__price', money(item.amount)));
        if (item.perSet) money_.appendChild(el('span', 'kq-package__per', item.perSet + ' / set'));
        main.appendChild(money_);
        card.appendChild(main);
        list.appendChild(card);
      });
      box.appendChild(list);
    }

    /* The recap does not ask again — it states the choice already made. */
    if (compact && pkg) {
      var chosen = el('p', 'kq-buybox__chosen');
      // Same two-part name the selector shows, so the recap names the tier
      // the parent actually picked rather than a shortened version of it.
      if (pkg.eyebrow) chosen.appendChild(el('span', 'kq-buybox__chosen-eyebrow', pkg.eyebrow));
      chosen.appendChild(el('span', 'kq-buybox__chosen-label', pkg.fullLabel || pkg.label));
      chosen.appendChild(el('span', 'kq-buybox__chosen-qty', pkg.qty + ' set'));
      box.appendChild(chosen);
    }

    /* 2 + 3. Price for the selected package, then the campaign and saving. */
    var priceWrap = el('div', 'kq-buybox__price');
    var percent = discountPercent(product);
    if (percent && step.campaignLabel) {
      var claimed = (step.campaignLabel.match(/\d+/) || [])[0];
      if (claimed && Math.abs(Number(claimed) - percent) <= 1) {
        priceWrap.appendChild(el('span', 'kq-buybox__campaign', step.campaignLabel));
      }
    }
    var row = el('div', 'kq-buybox__price-row');
    row.appendChild(el('span', 'kq-buybox__now', pkg ? money(pkg.amount) : product.price || ''));
    if (pkg && pkg.wasAmount) row.appendChild(el('span', 'kq-buybox__was', money(pkg.wasAmount)));
    else if (!pkg && product.compareAtPrice) row.appendChild(el('span', 'kq-buybox__was', product.compareAtPrice));
    priceWrap.appendChild(row);
    if (pkg && pkg.savesAmount) {
      priceWrap.appendChild(el('p', 'kq-buybox__saved', money(pkg.savesAmount) + ' tasarruf ediyorsunuz'));
    }
    box.appendChild(priceWrap);

    /* 4. Gifts as picture cards. */
    var bonuses = this.buildBonuses(step, compact);
    if (bonuses) box.appendChild(bonuses);

    /* 5. Guarantee, as one strip rather than a bordered card of its own. */
    if (step.guaranteeStrip) {
      var strip = el('div', 'kq-guard-strip');
      strip.appendChild(el('span', 'kq-guard-strip__seal', '21'));
      var sbody = el('span', 'kq-guard-strip__body');
      sbody.appendChild(el('span', 'kq-guard-strip__title', step.guaranteeStrip.title));
      if (step.guaranteeStrip.text && !compact) {
        sbody.appendChild(el('span', 'kq-guard-strip__text', step.guaranteeStrip.text));
      }
      strip.appendChild(sbody);
      box.appendChild(strip);
    }

    /* 6. CTA, then two checks and nothing else — no closing paragraph. */
    box.appendChild(this.buildOfferCta(step, product, context, pkg));

    if (step.reassurance && step.reassurance.length) {
      var re = el('ul', 'kq-reassure');
      step.reassurance.forEach(function (line) {
        var li = el('li', 'kq-reassure__item');
        li.appendChild(el('span', 'kq-reassure__check', '✓'));
        li.appendChild(el('span', null, line));
        re.appendChild(li);
      });
      box.appendChild(re);
    }
    return box;
  };

  /**
   * The free items, as things you can see rather than a list you have to
   * read. Both thumbnails are REAL store files, resolved through
   * `componentImage` — the pen set's own product photo and the guide's own
   * printed cover. If a key ever goes missing the card renders without a
   * picture rather than borrowing an unrelated one.
   */
  QuizApp.prototype.buildBonuses = function (step, compact) {
    var items = step.bonuses || [];
    if (!items.length) return null;

    var self = this;
    var wrap = el('div', 'kq-bonuses' + (compact ? ' kq-bonuses--compact' : ''));
    if (step.bonusHeading) wrap.appendChild(el('p', 'kq-bonuses__heading', step.bonusHeading));

    var grid = el('ul', 'kq-bonus-grid');
    items.forEach(function (bonus) {
      var li = el('li', 'kq-bonus');
      var thumb = self.buildComponentThumb(bonus.imageKey, bonus.name, 'kq-bonus__thumb');
      if (thumb) {
        if (bonus.tag) thumb.appendChild(el('span', 'kq-bonus__tag', bonus.tag));
        li.appendChild(thumb);
      }

      var body = el('span', 'kq-bonus__body');
      body.appendChild(el('span', 'kq-bonus__name', bonus.name));
      if (bonus.note && !compact) body.appendChild(el('span', 'kq-bonus__note', bonus.note));
      li.appendChild(body);
      grid.appendChild(li);
    });
    wrap.appendChild(grid);
    return wrap;
  };

  /**
   * A thumbnail of ONE component, from that component's own store file.
   * Returns null when the key resolves to nothing, so a missing asset shows
   * as a missing picture instead of the full-set photo quietly standing in
   * for a single notebook.
   */
  QuizApp.prototype.buildComponentThumb = function (key, alt, className) {
    var src = key && (this.config.componentImages || {})[key];
    if (!src) return null;

    var thumb = el('span', className);
    var img = document.createElement('img');
    img.src = src;
    img.alt = alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 240;
    img.height = 240;
    thumb.appendChild(img);
    return thumb;
  };

  /**
   * Star rating read from the product's own review metafields. Renders
   * nothing at all when the store has no rating data — a rating is never
   * fabricated to fill the slot.
   */
  QuizApp.prototype.buildOfferRating = function (step, product) {
    var value = Number(product.rating);
    if (!isFinite(value) || value <= 0) return null;

    var row = el('div', 'kq-offer-rating');
    var stars = buildStars(Math.round(value));
    if (stars) row.appendChild(stars);
    row.appendChild(el('span', 'kq-offer-rating__value', String(product.rating).replace('.', ',')));

    var count = Number(product.ratingCount);
    if (isFinite(count) && count > 0) {
      /* Turkish thousands separator, so 1246 reads as 1.246. */
      var formatted = count.toLocaleString ? count.toLocaleString('tr-TR') : String(count);
      row.appendChild(el('span', 'kq-offer-rating__count', '· ' + formatted + ' değerlendirme'));
    }
    return row;
  };

  /** Swipeable gallery of the PDP's own product photos. */
  QuizApp.prototype.buildOfferGallery = function (product) {
    var images = (product.images || []).filter(Boolean);
    if (!images.length) {
      if (!product.image) return null;
      images = [product.image];
    }

    var wrap = el('div', 'kq-offer-gallery-wrap');
    var track = el('div', 'kq-offer-gallery');
    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', product.title || 'Ürün görselleri');

    images.forEach(function (src, index) {
      var figure = el('figure', 'kq-offer-gallery__slide');
      var img = document.createElement('img');
      img.src = src;
      img.alt = product.title || '';
      /* First frame is above the fold; the rest stay lazy. */
      img.loading = index === 0 ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.width = 640;
      img.height = 640;
      figure.appendChild(img);
      track.appendChild(figure);
    });

    wrap.appendChild(track);
    attachCarouselNav(wrap, track);
    /* The first frame must be the product's own featured photo. A horizontal
       scroller can come back from bfcache or a re-render holding a restored
       scrollLeft, which would open the gallery mid-way through — on this
       product that means opening on an explainer graphic instead of the set. */
    track.scrollLeft = 0;
    return wrap;
  };

  /* --- SECTION 2: what you get -------------------------------------------
   * Picture-led: each card leads with THAT component's own photo, so a parent
   * can see the three notebooks are three different books without reading a
   * word. The two free items keep the mint treatment and a HEDİYE badge so
   * they never blend into the paid contents.
   * --------------------------------------------------------------------- */
  QuizApp.prototype.buildOfferContents = function (step) {
    if (!step.contents || !step.contents.length) return null;
    var self = this;
    var section = el('section', 'kq-offer-block');
    if (step.contentsHeading) section.appendChild(el('h2', 'kq-offer-block__title', step.contentsHeading));

    var list = el('ul', 'kq-offer-contents');
    step.contents.forEach(function (item) {
      var li = el('li', 'kq-offer-content' + (item.highlight ? ' is-highlight' : ''));
      var thumb = self.buildComponentThumb(item.imageKey, item.name, 'kq-offer-content__thumb');
      if (thumb) li.appendChild(thumb);
      var body = el('div', 'kq-offer-content__body');
      if (item.badge) body.appendChild(el('span', 'kq-offer-content__badge', item.badge));
      body.appendChild(el('h3', 'kq-offer-content__name', item.name));
      if (item.why) body.appendChild(el('p', 'kq-offer-content__why', item.why));
      li.appendChild(body);
      list.appendChild(li);
    });
    section.appendChild(list);
    return section;
  };

  /* --- SECTION 4: guarantee ---------------------------------------------- */
  QuizApp.prototype.buildOfferGuarantee = function (step) {
    var g = step.guarantee;
    if (!g) return null;

    var section = el('section', 'kq-offer-block kq-offer-guarantee');
    section.setAttribute('data-kq-guarantee', '');

    var card = el('div', 'kq-offer-guarantee__card');
    card.appendChild(el('span', 'kq-offer-guarantee__seal', '21'));
    if (g.heading) card.appendChild(el('h2', 'kq-offer-guarantee__title', g.heading));
    if (g.promise) card.appendChild(el('p', 'kq-offer-guarantee__promise', g.promise));

    if (g.steps && g.steps.length) {
      var steps = el('ol', 'kq-offer-guarantee__steps');
      g.steps.forEach(function (text, index) {
        var li = el('li', 'kq-offer-guarantee__step');
        li.appendChild(el('span', 'kq-offer-guarantee__step-no', String(index + 1)));
        li.appendChild(el('span', null, text));
        steps.appendChild(li);
      });
      card.appendChild(steps);
    }

    /* Approved stat, already used elsewhere in this same funnel. */
    if (g.stat) {
      var stat = el('p', 'kq-offer-guarantee__stat', g.stat);
      if (g.statFollow) {
        stat.appendChild(document.createElement('br'));
        stat.appendChild(el('strong', null, g.statFollow));
      }
      card.appendChild(stat);
    }

    /* Real policy conditions, shown rather than buried. */
    if (g.conditions && g.conditions.length) {
      var details = document.createElement('details');
      details.className = 'kq-offer-guarantee__terms';
      var summary = document.createElement('summary');
      summary.textContent = 'Garanti koşulları';
      details.appendChild(summary);
      var ul = el('ul', 'kq-offer-guarantee__conditions');
      g.conditions.forEach(function (entry) {
        /* A condition is either a plain string or {text, strong} — the
           flagged one is the answer to the objection, not fine print. */
        var isObject = entry && typeof entry === 'object';
        var li = el('li', isObject && entry.strong ? 'is-strong' : null, isObject ? entry.text : entry);
        ul.appendChild(li);
      });
      details.appendChild(ul);
      if (g.howTo) details.appendChild(el('p', 'kq-offer-guarantee__howto', g.howTo));

      if (g.howToAction && g.howToAction.url) {
        var wa = el('a', 'kq-button kq-button--whatsapp kq-offer-guarantee__wa');
        wa.href = g.howToAction.url;
        wa.target = '_blank';
        wa.rel = 'noopener';
        wa.appendChild(whatsappIcon());
        wa.appendChild(el('span', null, g.howToAction.label));
        details.appendChild(wa);
        if (g.howToAction.note) {
          details.appendChild(el('p', 'kq-offer-guarantee__wa-note', g.howToAction.note));
        }
      }
      if (g.legalNote) details.appendChild(el('p', 'kq-offer-guarantee__legal', g.legalNote));
      card.appendChild(details);
    }

    section.appendChild(card);
    return section;
  };

  /* --- SECTION 5: objection-based social proof ---------------------------- */
  QuizApp.prototype.buildOfferReviews = function (step) {
    var reviews = (this.config.customerReviews || []).filter(function (review) {
      return review && review.text;
    });
    if (!reviews.length) return null;

    var byAuthor = {};
    reviews.forEach(function (review) {
      if (review.author) byAuthor[review.author] = review;
    });

    /* Curated order + objection labels over real reviews. A label whose
       review is missing from live data is skipped; a live review with no
       label still renders, unlabelled — real proof is never dropped. */
    var ordered = [];
    var used = {};
    (step.reviewObjections || []).forEach(function (entry) {
      var review = byAuthor[entry.author];
      if (review && !used[entry.author]) {
        ordered.push({ review: review, label: entry.label, excerpt: entry.excerpt });
        used[entry.author] = true;
      }
    });
    reviews.forEach(function (review) {
      if (!review.author || !used[review.author]) {
        ordered.push({ review: review, label: null });
        if (review.author) used[review.author] = true;
      }
    });
    /* Kept deliberately short: the funnel already showed the full proof
       section before this screen. */
    if (step.reviewLimit) ordered = ordered.slice(0, step.reviewLimit);
    if (!ordered.length) return null;

    var section = el('section', 'kq-offer-block');
    if (step.reviewsHeading) section.appendChild(el('h2', 'kq-offer-block__title', step.reviewsHeading));

    var wrap = el('div', 'kq-carousel-wrap');
    var track = el('div', 'kq-carousel');
    track.setAttribute('tabindex', '0');
    track.setAttribute('role', 'group');
    track.setAttribute('aria-label', step.reviewsHeading || 'Müşteri yorumları');
    track.setAttribute('data-kq-reviews', '');

    ordered.forEach(function (entry) {
      var review = entry.review;
      var card = el('figure', 'kq-review');
      if (entry.label) card.appendChild(el('p', 'kq-proof-tag', entry.label));
      var stars = buildStars(review.stars);
      if (stars) card.appendChild(stars);
      card.appendChild(el('blockquote', 'kq-review__text', '“' + (entry.excerpt || review.text) + '”'));

      var footer = el('figcaption', 'kq-review__footer');
      if (review.photo) {
        var photo = document.createElement('img');
        photo.className = 'kq-review__photo';
        photo.src = review.photo;
        photo.alt = review.author ? review.author + ' – müşteri fotoğrafı' : 'Müşteri fotoğrafı';
        photo.loading = 'lazy';
        photo.decoding = 'async';
        photo.width = 40;
        photo.height = 40;
        footer.appendChild(photo);
      }
      var meta = el('div', 'kq-review__meta');
      if (review.author) meta.appendChild(el('span', 'kq-review__author', review.author));
      /* Only ever shown when the store's own data says so. */
      if (review.verified) meta.appendChild(el('span', 'kq-review__verified', 'Doğrulanmış alışveriş'));
      footer.appendChild(meta);
      card.appendChild(footer);
      track.appendChild(card);
    });

    wrap.appendChild(track);
    section.appendChild(wrap);
    attachCarouselNav(wrap, track);
    return section;
  };

  /* --- SECTION 5: final buy box ------------------------------------------ */
  QuizApp.prototype.buildOfferRecap = function (step, product) {
    var section = el('section', 'kq-offer-block kq-offer-recap');
    if (step.recapHeading) section.appendChild(el('h2', 'kq-offer-block__title', step.recapHeading));
    if (step.productName) section.appendChild(el('p', 'kq-offer-recap__product', step.productName));
    /* Condensed repeat of the same module, not a new content section. */
    section.appendChild(this.buildBuyBox(step, product, 'recap'));
    return section;
  };

  /* --- SECTION 7: FAQ ----------------------------------------------------- */
  QuizApp.prototype.buildOfferFaq = function (step) {
    if (!step.faq || !step.faq.length) return null;
    var section = el('section', 'kq-offer-block kq-offer-faq');
    if (step.faqHeading) section.appendChild(el('h2', 'kq-offer-block__title', step.faqHeading));

    step.faq.forEach(function (entry) {
      var details = document.createElement('details');
      details.className = 'kq-offer-faq__item';
      var summary = document.createElement('summary');
      summary.className = 'kq-offer-faq__q';
      summary.textContent = entry.q;
      details.appendChild(summary);
      details.appendChild(el('p', 'kq-offer-faq__a', entry.a));
      section.appendChild(details);
    });
    return section;
  };

  /**
   * Sticky mobile purchase bar. Lives on the section root (not inside the
   * animated screen wrapper, whose transform would break position:fixed)
   * and only appears once the hero CTA has scrolled out of view.
   */
  QuizApp.prototype.mountStickyCta = function (step, product) {
    this.clearStickyCta();
    if (!product.variantId || !product.available) return;

    var bar = el('div', 'kq-offer-sticky');
    bar.setAttribute('data-kq-sticky', '');

    /* Reflects the CURRENT package choice, so the bar can never advertise
       a price different from the one the CTA will add. */
    var pkg = this.selectedPackage(step);
    var info = el('div', 'kq-offer-sticky__info');
    /* The campaign label rides above the name on its own line rather than
       inline with it: the name line truncates, and an inline prefix would be
       the part that survived while the actual package name got cut off. */
    if (pkg && pkg.eyebrow) info.appendChild(el('span', 'kq-offer-sticky__eyebrow', pkg.eyebrow));
    info.appendChild(
      el(
        'span',
        'kq-offer-sticky__name',
        pkg ? (pkg.fullLabel || pkg.label) + ' · ' + pkg.qty + ' set' : step.productName || product.title || ''
      )
    );
    var priceRow = el('span', 'kq-offer-sticky__price');
    priceRow.appendChild(el('span', 'kq-offer-sticky__now', pkg ? money(pkg.amount) : product.price || ''));
    if (pkg && pkg.wasAmount) priceRow.appendChild(el('span', 'kq-offer-sticky__was', money(pkg.wasAmount)));
    else if (!pkg && product.compareAtPrice) priceRow.appendChild(el('span', 'kq-offer-sticky__was', product.compareAtPrice));
    info.appendChild(priceRow);
    bar.appendChild(info);

    var button = el('button', 'kq-button kq-button--primary kq-offer-sticky__button');
    button.type = 'button';
    button.setAttribute('data-kq-action', 'atc-offer');
    button.setAttribute('data-kq-context', 'sticky');
    button.appendChild(el('span', null, step.cta || '21 Günlük Gelişime Başla'));
    bar.appendChild(button);

    this.root.appendChild(bar);
    this.stickyBar = bar;

    /* Both buy boxes — the hero's and the recap's. The bar stands in for a
       buy box that is off screen, so it stays hidden while either one is on
       screen at all, not merely while its button is. Watching just the hero
       CTA meant the bar showed over the top of the page before the visitor
       had scrolled anywhere, and again on top of the recap buy box, both
       times repeating the package and price already on screen. */
    var boxes = [].slice.call(this.root.querySelectorAll('.kq-offer [data-kq-buybox]'));
    if (!boxes.length) return;

    /* Deliberately a scroll check, not an IntersectionObserver: IO only
       fires when an element CROSSES a threshold, so a fast scroll (or a
       restored mid-page position) can take a buy box from below the viewport
       to above it without ever intersecting — and the bar would never appear.
       Reading positions directly has no such gap, and it re-hides the bar the
       moment a buy box scrolls back into view. */
    var ticking = false;
    var evaluate = function () {
      ticking = false;
      var height = global.innerHeight || document.documentElement.clientHeight;
      var anyVisible = boxes.some(function (node) {
        var rect = node.getBoundingClientRect();
        return rect.bottom > 0 && rect.top < height;
      });
      bar.classList.toggle('is-visible', !anyVisible);
    };
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      global.requestAnimationFrame(evaluate);
    };

    global.addEventListener('scroll', onScroll, { passive: true });
    global.addEventListener('resize', onScroll, { passive: true });
    this.offerListeners.push({ node: global, type: 'scroll', fn: onScroll });
    this.offerListeners.push({ node: global, type: 'resize', fn: onScroll });
    evaluate();
  };

  QuizApp.prototype.clearStickyCta = function () {
    if (this.stickyBar && this.stickyBar.parentNode) {
      this.stickyBar.parentNode.removeChild(this.stickyBar);
    }
    this.stickyBar = null;
  };

  /** Fires the guarantee-view and review-interaction events, once each. */
  QuizApp.prototype.watchOfferEngagement = function () {
    var self = this;

    var guarantee = this.root.querySelector('[data-kq-guarantee]');
    if (guarantee && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.analytics.emit(
              'quiz_offer_guarantee_view',
              self.offerPayload(),
              'offer_guarantee_view'
            );
            obs.disconnect();
          });
        },
        { threshold: 0.4 }
      );
      observer.observe(guarantee);
      this.offerObservers.push(observer);
    }

    var reviews = this.root.querySelector('[data-kq-reviews]');
    if (reviews) {
      var wrap = reviews.parentNode;
      var fire = function () {
        self.analytics.emit(
          'quiz_offer_review_interaction',
          self.offerPayload(),
          'offer_review_interaction'
        );
      };

      /* A scroll-snap track emits a scroll event during initial layout, so
         a bare scroll listener would report an "interaction" for every
         visitor who never touched the carousel. Only a real change in
         scroll position counts. */
      var restLeft = reviews.scrollLeft;
      var onScroll = function () {
        if (reviews.scrollLeft !== restLeft) fire();
      };
      /* Arrow / dot clicks and keyboard paging are interactions too, and
         they can settle back to the same offset. */
      var onPoint = function () {
        fire();
      };

      reviews.addEventListener('scroll', onScroll, { passive: true });
      reviews.addEventListener('keydown', onPoint);
      this.offerListeners.push({ node: reviews, type: 'scroll', fn: onScroll });
      this.offerListeners.push({ node: reviews, type: 'keydown', fn: onPoint });
      if (wrap) {
        wrap.addEventListener('pointerdown', onPoint);
        this.offerListeners.push({ node: wrap, type: 'pointerdown', fn: onPoint });
      }
    }
  };

  QuizApp.prototype.offerPayload = function () {
    var product = this.config.product || {};
    return Object.assign(this.basePayload(), {
      diagnosis: this.profile && this.profile.diagnosis,
      need: this.profile && this.profile.need,
      variant_id: product.variantId,
      product_handle: product.handle
    });
  };

  /** Tears down everything the offer screen attached. */
  QuizApp.prototype.teardownOffer = function () {
    (this.offerObservers || []).forEach(function (observer) {
      observer.disconnect();
    });
    this.offerObservers = [];
    (this.offerListeners || []).forEach(function (entry) {
      entry.node.removeEventListener(entry.type, entry.fn);
    });
    this.offerListeners = [];
    this.clearStickyCta();
  };

  /**
   * Renders authored prose blocks. `html` is static copy defined in this file,
   * never anything the visitor supplied.
   */
  QuizApp.prototype.buildStepBlocks = function (blocks) {
    if (!blocks || !blocks.length) return null;
    var section = el('section', 'kq-card kq-explain kq-explain--lead');
    var self = this;
    blocks.forEach(function (block) {
      if (block.type === 'video') {
        var media = self.buildVideo({ src: block.src, aspect: block.aspect, alt: block.alt });
        if (media) {
          media.classList.add('kq-video--inline');
          section.appendChild(media);
        }
        return;
      }
      if (block.type === 'quote') {
        var quote = document.createElement('blockquote');
        quote.className = 'kq-pullquote';
        var line = document.createElement('p');
        line.innerHTML = block.html;
        quote.appendChild(line);
        section.appendChild(quote);
        return;
      }
      var paragraph = document.createElement('p');
      paragraph.innerHTML = block.html;
      section.appendChild(paragraph);
    });
    return section;
  };

  /**
   * Side-by-side page comparison. Every image resolves through the merchant
   * config; a missing source drops that half rather than rendering a broken
   * frame, and the pair renders only when both halves survive — one page on
   * its own makes no comparison. Square boxes are reserved up front so the
   * screen does not shift as the images decode.
   */
  QuizApp.prototype.buildCompare = function (step) {
    var compare = step.compare;
    if (!compare || !compare.items || !compare.items.length) return null;

    var sources = this.config.compareImages || {};
    var items = compare.items.filter(function (item) {
      return Boolean(sources[item.imageKey]);
    });
    if (items.length < compare.items.length) return null;

    var section = el('section', 'kq-compare');
    if (compare.note) {
      var note = el('p', 'kq-compare__note', compare.note);
      section.appendChild(note);
    }

    var grid = el('div', 'kq-compare__grid');
    items.forEach(function (item) {
      var figure = el('figure', 'kq-compare__item');
      var image = document.createElement('img');
      image.className = 'kq-compare__img';
      image.src = sources[item.imageKey];
      image.alt = item.alt || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 800;
      image.height = 800;
      figure.appendChild(image);
      grid.appendChild(figure);
    });

    section.appendChild(grid);

    if (compare.caption) {
      section.appendChild(el('p', 'kq-compare__caption', compare.caption));
    }

    return section;
  };

  /**
   * Square, muted, inline video sized by aspect-ratio so nothing shifts while
   * it loads. Under reduced-motion it stays on the poster and exposes controls
   * instead of forcing playback.
   */
  QuizApp.prototype.buildStepVideo = function (step) {
    var sources = this.config.videos || {};
    var source = sources[step.videoKey];
    if (!source || !source.src) return null;
    return this.buildVideo({ src: source.src, poster: source.poster, alt: step.videoAlt });
  };

  /** Muted, inline, looping video in the result-page container. */
  QuizApp.prototype.buildVideo = function (options) {
    var source = options || {};
    if (!source.src) return null;

    var figure = el('figure', 'kq-video');
    if (source.aspect) figure.style.setProperty('--kq-video-aspect', source.aspect);
    var video = document.createElement('video');
    video.className = 'kq-video__el';
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('loop', '');
    video.setAttribute('preload', 'metadata');
    video.setAttribute('disablepictureinpicture', '');
    // The box is reserved by aspect-ratio in CSS, so no intrinsic attrs are
    // needed and none are assumed about the source.
    if (source.poster) video.poster = source.poster;
    if (source.alt) video.setAttribute('aria-label', source.alt);

    var src = source.src;
    if (this.prefersReducedMotion) {
      video.controls = true;
      video.removeAttribute('loop');
      video.loop = false;
      // With no poster, seeking to the first frame gives something to look at
      // instead of an empty box.
      if (!source.poster) src += '#t=0.1';
    } else {
      video.setAttribute('autoplay', '');
    }

    var mp4 = document.createElement('source');
    mp4.src = src;
    mp4.type = 'video/mp4';
    video.appendChild(mp4);

    figure.appendChild(video);
    return figure;
  };

  QuizApp.prototype.playStepVideo = function () {
    if (this.prefersReducedMotion) return;
    this.root.querySelectorAll('.kq-video__el').forEach(function (video) {
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        // a blocked autoplay is not an error worth shouting about
        attempt.catch(function () {
          video.controls = true;
        });
      }
    });
  };

  /** The original single-page result, used by every category without a flow. */
  QuizApp.prototype.renderResultPage = function () {
    var profile = this.profile;

    this.setProgress(null);
    this.setBackVisible(false);

    var wrap = el('div', 'kq-screen kq-result');
    var copy = this.config.copy || {};
    var results = this.config.results || {};
    var content = results[profile.diagnosis] || {};
    var labels = this.config.labels || {};

    /* 1 — personalized diagnosis headline */
    var head = el('header', 'kq-result__head');
    head.appendChild(el('p', 'kq-eyebrow', copy.resultEyebrow || 'Cevaplarınıza göre'));
    var headline = el('h1', 'kq-result__title');
    headline.setAttribute('data-kq-focus', '');
    headline.setAttribute('tabindex', '-1');
    headline.textContent =
      content.headline ||
      'Çocuğunuzun yazı sürecinde en çok zorlandığı alan: ' + (labels[profile.diagnosis] || '');
    head.appendChild(headline);
    if (content.subheadline) head.appendChild(el('p', 'kq-result__sub', content.subheadline));
    wrap.appendChild(head);

    /* profile strip — customer-facing labels only, never internal keys */
    wrap.appendChild(this.buildScoreStrip(profile, labels));

    /* 2 — recognition */
    var recognition = this.buildRecognition(profile);
    if (recognition) wrap.appendChild(recognition);

    /* diagnosis body */
    if (content.body) {
      var body = el('div', 'kq-card kq-rte');
      body.innerHTML = content.body;
      wrap.appendChild(body);
    }

    if (content.image) {
      var figure = el('figure', 'kq-result__figure');
      var img = document.createElement('img');
      img.src = content.image;
      img.alt = content.imageAlt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      if (content.imageWidth) img.width = content.imageWidth;
      if (content.imageHeight) img.height = content.imageHeight;
      figure.appendChild(img);
      wrap.appendChild(figure);
    }

    /* 3 — explanation / realization */
    if (copy.explanationBody) {
      var explanation = el('section', 'kq-card kq-explain');
      if (copy.explanationHeading) explanation.appendChild(el('h2', 'kq-h3', copy.explanationHeading));
      splitLines(copy.explanationBody).forEach(function (line) {
        explanation.appendChild(el('p', null, line));
      });
      wrap.appendChild(explanation);
    }

    /* 4 — mechanism bridge */
    wrap.appendChild(this.buildMechanism());

    /* 5 — Q9 personalization */
    var needCopy = (this.config.needs || {})[profile.need];
    if (needCopy) {
      var need = el('section', 'kq-card kq-need');
      if (copy.needHeading) need.appendChild(el('h2', 'kq-h3', copy.needHeading));
      splitLines(needCopy).forEach(function (line) {
        need.appendChild(el('p', null, line));
      });
      wrap.appendChild(need);
    }

    /* guarantee — real store policy only */
    var guarantee = this.config.guarantee || {};
    var showGuarantee = guarantee.enabled && (guarantee.always || profile.need === 'risk_reversal');
    if (showGuarantee && guarantee.text) {
      var box = el('section', 'kq-card kq-guarantee');
      if (guarantee.heading) box.appendChild(el('h2', 'kq-h3', guarantee.heading));
      splitLines(guarantee.text).forEach(function (line) {
        box.appendChild(el('p', null, line));
      });
      if (guarantee.linkUrl && guarantee.linkLabel) {
        var link = el('a', 'kq-textlink', guarantee.linkLabel);
        link.href = guarantee.linkUrl;
        box.appendChild(link);
      }
      wrap.appendChild(box);
    }

    /* 6 — personalized desired outcome (echoes the parent's own words) */
    var desired = findOption(this.branch, 'q8', this.state.answers.q8);
    if (desired) {
      var outcome = el('section', 'kq-card kq-outcome');
      outcome.appendChild(el('p', 'kq-outcome__label', copy.outcomeLabel || 'Sizin hedefiniz'));
      outcome.appendChild(el('p', 'kq-outcome__quote', desired.text));
      if (copy.outcomeNote) outcome.appendChild(el('p', 'kq-outcome__note', copy.outcomeNote));
      wrap.appendChild(outcome);
    }

    /* 7 — CTA area */
    wrap.appendChild(this.buildCta());

    var restart = el('button', 'kq-textlink kq-restart', copy.restartLabel || 'Testi baştan al');
    restart.type = 'button';
    restart.setAttribute('data-kq-action', 'restart');
    wrap.appendChild(restart);

    this.swap(wrap, { announce: headline.textContent });
  };

  /** Payload shared by every completion event, staged flow or single page. */
  QuizApp.prototype.resultPayload = function () {
    var profile = this.profile;
    return Object.assign(this.basePayload(), {
      diagnosis: profile.diagnosis,
      resolved_by: profile.resolvedBy,
      need: profile.need,
      desired_outcome: profile.desiredOutcome,
      hypothesis: profile.hypothesis,
      failed_attempts: profile.failedAttempts.join(','),
      urgency: profile.urgency.join(','),
      score_kalem: profile.scores.kalem,
      score_okunaklilik: profile.scores.okunaklilik,
      score_istek: profile.scores.istek,
      score_ozguven: profile.scores.ozguven
    });
  };

  QuizApp.prototype.buildScoreStrip = function (profile, labels) {
    var strip = el('div', 'kq-scores');
    var self = this;
    CATEGORIES.forEach(function (category) {
      var row = el('div', 'kq-scores__row');
      if (category === profile.diagnosis) row.classList.add('is-primary');

      row.appendChild(el('span', 'kq-scores__label', labels[category] || category));

      var dots = el('span', 'kq-scores__dots');
      var score = profile.scores[category];
      dots.setAttribute('aria-label', score + ' / 2');
      for (var i = 0; i < 2; i++) {
        var dot = el('span', 'kq-scores__dot' + (i < score ? ' is-on' : ''));
        dot.setAttribute('aria-hidden', 'true');
        dots.appendChild(dot);
      }
      row.appendChild(dots);
      strip.appendChild(row);
    });
    if (self.config.copy && self.config.copy.scoresNote) {
      strip.appendChild(el('p', 'kq-scores__note', self.config.copy.scoresNote));
    }
    return strip;
  };

  /**
   * Recognition — quotes back what the parent actually selected, so the result
   * reads as personalized rather than category-swapped.
   */
  QuizApp.prototype.buildRecognition = function (profile) {
    var self = this;
    var quotes = [];

    OBSERVATION_QUESTIONS.forEach(function (questionId) {
      var option = findOption(self.branch, questionId, self.state.answers[questionId]);
      if (option && option.category === profile.diagnosis) quotes.push(option.text);
    });

    var firstOf = function (questionId) {
      var selected = self.state.answers[questionId] || [];
      if (!selected.length) return null;
      var option = findOption(self.branch, questionId, selected[0]);
      return option ? option.text : null;
    };

    var attempt = firstOf('q6');
    var urgency = firstOf('q7');
    if (attempt) quotes.push(attempt);
    if (urgency && quotes.length < 3) quotes.push(urgency);

    quotes = quotes.slice(0, 3);
    if (!quotes.length) return null;

    var copy = this.config.copy || {};
    var section = el('section', 'kq-card kq-recognition');
    if (copy.recognitionHeading) section.appendChild(el('h2', 'kq-h3', copy.recognitionHeading));

    var list = el('ul', 'kq-recognition__list');
    quotes.forEach(function (quote) {
      list.appendChild(el('li', 'kq-recognition__item', quote));
    });
    section.appendChild(list);

    if (copy.recognitionNote) section.appendChild(el('p', 'kq-recognition__note', copy.recognitionNote));
    return section;
  };

  QuizApp.prototype.buildMechanism = function () {
    var mechanism = this.config.mechanism || {};
    var section = el('section', 'kq-mechanism');

    if (mechanism.heading) section.appendChild(el('h2', 'kq-h2', mechanism.heading));
    if (mechanism.intro) section.appendChild(el('p', 'kq-mechanism__intro', mechanism.intro));

    var grid = el('div', 'kq-mechanism__grid');

    var column = function (modifier, title, steps, guided) {
      var col = el('div', 'kq-mechanism__col ' + modifier);

      var art = document.createElement('div');
      art.innerHTML = loopArt(guided);
      if (art.firstChild) col.appendChild(art.firstChild);

      col.appendChild(el('p', 'kq-mechanism__title', title));
      var list = el('ol', 'kq-mechanism__steps');
      steps.forEach(function (step) {
        list.appendChild(el('li', null, step));
      });
      col.appendChild(list);
      return col;
    };

    grid.appendChild(
      column(
        'kq-mechanism__col--plain',
        mechanism.plainTitle || 'Normal çalışma',
        splitLines(mechanism.plainSteps),
        false
      )
    );
    grid.appendChild(
      column(
        'kq-mechanism__col--kidslar',
        mechanism.kidslarTitle || 'Kidslar Magic Learn',
        splitLines(mechanism.kidslarSteps),
        true
      )
    );

    section.appendChild(grid);
    if (mechanism.footnote) section.appendChild(el('p', 'kq-mechanism__footnote', mechanism.footnote));
    return section;
  };

  QuizApp.prototype.buildCta = function () {
    var product = this.config.product || {};
    var copy = this.config.copy || {};
    var section = el('section', 'kq-cta');

    if (!product.url) {
      section.appendChild(
        el('p', 'kq-error', 'Ürün bağlantısı ayarlanmamış. Tema düzenleyicisinden ürünü seçin.')
      );
      return section;
    }

    var card = el('div', 'kq-cta__product');
    if (product.image) {
      var img = document.createElement('img');
      img.className = 'kq-cta__image';
      img.src = product.image;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 160;
      img.height = 160;
      card.appendChild(img);
    }

    var info = el('div', 'kq-cta__info');
    info.appendChild(el('p', 'kq-cta__title', product.title || ''));
    if (product.price) info.appendChild(el('p', 'kq-cta__price', product.price));
    card.appendChild(info);
    section.appendChild(card);

    var actions = el('div', 'kq-cta__actions');

    var primary = el('a', 'kq-button kq-button--primary', copy.primaryCta || "Kidslar Magic Learn'ı İncele");
    primary.href = withAttribution(product.url, this.state.attribution);
    primary.setAttribute('data-kq-action', 'pdp');
    actions.appendChild(primary);

    if (product.variantId && product.available) {
      var secondary = el('button', 'kq-button kq-button--secondary', copy.atcCta || 'Sepete Ekle');
      secondary.type = 'button';
      secondary.setAttribute('data-kq-action', 'atc');
      actions.appendChild(secondary);
    } else if (product.variantId && !product.available) {
      actions.appendChild(el('p', 'kq-cta__soldout', copy.soldOut || 'Şu anda stokta yok.'));
    }

    section.appendChild(actions);

    var status = el('p', 'kq-cta__status');
    status.setAttribute('data-kq-cart-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    section.appendChild(status);

    var trust = this.config.trust || [];
    if (trust.length) {
      var list = el('ul', 'kq-trust');
      trust.forEach(function (item) {
        list.appendChild(el('li', 'kq-trust__item', item));
      });
      section.appendChild(list);
    }

    return section;
  };

  /* ---------- interaction ---------- */

  QuizApp.prototype.onRootClick = function (event) {
    var trigger = event.target.closest('[data-kq-action]');
    if (!trigger || !this.root.contains(trigger)) return;

    var action = trigger.getAttribute('data-kq-action');

    if (action === 'start') {
      event.preventDefault();
      this.restart();
      this.start();
      return;
    }
    if (action === 'resume') {
      event.preventDefault();
      this.renderQuestion(Math.min(this.state.index, this.questions.length - 1));
      return;
    }
    if (action === 'answer') {
      event.preventDefault();
      this.onAnswer(trigger);
      return;
    }
    if (action === 'next') {
      event.preventDefault();
      this.advance();
      return;
    }
    if (action === 'result-next') {
      event.preventDefault();
      this.renderResultStep((this.state.resultStep || 0) + 1);
      return;
    }
    if (action === 'restart') {
      event.preventDefault();
      this.restart();
      return;
    }
    if (action === 'pdp') {
      this.analytics.emit(
        'quiz_pdp_click',
        Object.assign(this.basePayload(), {
          diagnosis: this.profile && this.profile.diagnosis,
          need: this.profile && this.profile.need
        })
      );
      return;
    }
    if (action === 'atc') {
      event.preventDefault();
      this.onAddToCart(trigger);
      return;
    }
    if (action === 'atc-offer') {
      event.preventDefault();
      this.onOfferAddToCart(trigger);
      return;
    }
    if (action === 'select-package') {
      event.preventDefault();
      this.onSelectPackage(trigger);
    }
  };

  /** Package choice re-renders the step so every price and CTA follows it. */
  QuizApp.prototype.onSelectPackage = function (button) {
    var id = button.getAttribute('data-package-id');
    if (!id || id === this.offerPackageId) return;
    this.offerPackageId = id;

    var flow = RESULT_FLOWS[this.profile.diagnosis] || [];
    var step = flow[this.state.resultStep];
    var pkg = this.selectedPackage(step || {});
    this.analytics.emit(
      'quiz_offer_package_select',
      Object.assign(this.offerPayload(), { package_id: id, quantity: pkg ? pkg.qty : null })
    );
    /* Re-render in place. The step rebuilds itself so every price, the CTA
       and the sticky bar follow the new choice — but the reader stays exactly
       where they were. Scrolling them back to the top of the offer after a
       tap on a package was the page throwing away their place mid-decision.
       Focus is restored by hand below, because the card they pressed is
       destroyed by the rebuild. */
    this.renderResultStep(this.state.resultStep, { scroll: false, focus: false });

    var reselected =
      this.stage &&
      this.stage.querySelector('[data-kq-action="select-package"][data-package-id="' + id + '"]');
    if (reselected) {
      try {
        reselected.focus({ preventScroll: true });
      } catch (error) {
        /* Older Safari ignores preventScroll and would scroll on focus —
           leaving focus where it is beats undoing the fix above. */
      }
    }
  };

  /**
   * Offer-screen add to cart. Same product, same variant and same native
   * cart path the PDP uses — this never creates a separate or synthetic
   * product. Quiz attribution (UTM/fbclid) already rides along inside
   * addToCart via the stored first-touch state.
   */
  QuizApp.prototype.onOfferAddToCart = function (button) {
    if (button.disabled) return;

    var product = this.config.product || {};
    var copy = this.config.copy || {};
    var context = button.getAttribute('data-kq-context') || 'hero';
    var self = this;

    /* Status lives next to whichever CTA was pressed; the sticky bar has
       none of its own, so it reports into the hero's. */
    var status =
      (button.closest('.kq-offer-cta') || this.root).querySelector('[data-kq-cart-status]') ||
      this.root.querySelector('[data-kq-cart-status]');

    if (!product.variantId || !product.available) {
      if (status) {
        status.textContent =
          copy.cartError || 'Ürün şu anda eklenemiyor. Lütfen ürün sayfasından deneyin.';
        status.classList.add('is-error');
      }
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error');
    }

    var flow = RESULT_FLOWS[this.profile.diagnosis] || [];
    var step = flow[this.state.resultStep] || {};
    var pkg = this.selectedPackage(step);
    var quantity = pkg ? pkg.qty : 1;

    var properties = {};
    if (this.profile && this.config.sendPropertiesToCart) {
      properties['_quiz_diagnosis'] = this.profile.diagnosis;
      properties['_quiz_need'] = this.profile.need;
    }

    addToCart(product.variantId, properties, quantity)
      .then(function () {
        if (status) status.textContent = copy.cartSuccess || 'Sepete eklendi.';
        self.analytics.emit(
          'quiz_offer_atc',
          Object.assign(self.offerPayload(), { cta_context: context, quantity: quantity, package_id: pkg ? pkg.id : null })
        );
        /* Existing funnel event kept so current reporting keeps working. */
        self.analytics.emit(
          'quiz_add_to_cart',
          Object.assign(self.offerPayload(), { quantity: quantity, product_title: product.title })
        );
      })
      .catch(function (error) {
        if (status) {
          var fallback = copy.cartError || 'Ürün sepete eklenemedi. Lütfen tekrar deneyin.';
          status.textContent = error && error.customerFacing && error.message ? error.message : fallback;
          status.classList.add('is-error');
        }
        console.error('[kidslar-quiz] offer add to cart failed', error);
      })
      .finally(function () {
        button.disabled = false;
        button.classList.remove('is-loading');
      });
  };

  QuizApp.prototype.onAnswer = function (button) {
    if (this.locked) return;

    var question = this.questions[this.state.index];
    if (!question) return;
    var optionId = button.getAttribute('data-kq-option');

    if (question.type === 'multi') {
      var selected = Array.isArray(this.state.answers[question.id])
        ? this.state.answers[question.id].slice()
        : [];
      var position = selected.indexOf(optionId);
      if (position === -1) selected.push(optionId);
      else selected.splice(position, 1);

      this.state.answers[question.id] = selected;
      saveState(this.state);

      button.classList.toggle('is-selected', position === -1);
      button.setAttribute('aria-checked', position === -1 ? 'true' : 'false');

      var next = this.root.querySelector('[data-kq-action="next"]');
      if (next) next.disabled = selected.length === 0;

      this.trackAnswer(question, optionId, position === -1 ? 'select' : 'deselect');
      return;
    }

    // single select — lock, paint, auto-advance
    this.locked = true;
    this.state.answers[question.id] = optionId;
    saveState(this.state);

    var options = this.root.querySelectorAll('[data-kq-action="answer"]');
    Array.prototype.forEach.call(options, function (option) {
      var isTarget = option === button;
      option.classList.toggle('is-selected', isTarget);
      option.setAttribute('aria-checked', isTarget ? 'true' : 'false');
    });

    this.trackAnswer(question, optionId, 'select');

    var self = this;
    this.later(function () {
      self.advance();
    }, 280);
  };

  QuizApp.prototype.trackAnswer = function (question, optionId, mode) {
    var option = findOption(this.branch, question.id, optionId);
    this.analytics.emit(
      'quiz_answer',
      Object.assign(this.basePayload(), {
        question_number: this.state.index + 1,
        question_id: question.id,
        answer_id: optionId,
        answer_category: (option && option.category) || null,
        answer_tag: (option && option.tag) || null,
        mode: mode
      })
    );
  };

  QuizApp.prototype.advance = function () {
    var nextIndex = this.state.index + 1;
    this.locked = false;

    if (nextIndex >= this.questions.length) {
      this.renderLoading();
      return;
    }
    this.renderQuestion(nextIndex);
  };

  QuizApp.prototype.onAddToCart = function (button) {
    if (button.disabled) return;

    var product = this.config.product || {};
    var status = this.root.querySelector('[data-kq-cart-status]');
    var copy = this.config.copy || {};
    var self = this;

    if (!product.variantId) {
      if (status) status.textContent = copy.cartError || 'Ürün şu anda eklenemiyor. Lütfen ürün sayfasından deneyin.';
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');
    if (status) {
      status.textContent = '';
      status.classList.remove('is-error');
    }

    var properties = {};
    if (this.profile && this.config.sendPropertiesToCart) {
      properties['_quiz_diagnosis'] = this.profile.diagnosis;
      properties['_quiz_need'] = this.profile.need;
    }

    addToCart(product.variantId, properties)
      .then(function () {
        if (status) status.textContent = copy.cartSuccess || 'Sepete eklendi.';
        self.analytics.emit(
          'quiz_add_to_cart',
          Object.assign(self.basePayload(), {
            diagnosis: self.profile && self.profile.diagnosis,
            need: self.profile && self.profile.need,
            variant_id: product.variantId,
            product_title: product.title
          })
        );
      })
      .catch(function (error) {
        if (status) {
          var fallback = copy.cartError || 'Ürün sepete eklenemedi. Lütfen tekrar deneyin.';
          status.textContent = error && error.customerFacing && error.message ? error.message : fallback;
          status.classList.add('is-error');
        }
        console.error('[kidslar-quiz] add to cart failed', error);
      })
      .finally(function () {
        button.disabled = false;
        button.classList.remove('is-loading');
      });
  };

  /* ========================================================================
   * BOOT — idempotent, Theme Editor safe
   * ====================================================================== */


  /* ======================================================================
   * TEST-PAGE ONLY — direct entry to the offer screen.
   *
   * This file is a dedicated copy used by /pages/kidslar-offer-test and by
   * nothing else. The production commerce test has to run Add to Cart many
   * times over; answering nine questions and five result screens before each
   * one is not a test, it is a chore that invites mis-clicks. `?kq_offer=1`
   * seeds a REAL answer set (validated offline: diagnose() returns
   * "okunaklilik") and renders the genuine offer step through the normal
   * machinery — no bypass of pricing, packages, cart or upsell code.
   *
   * Without the parameter the page behaves exactly like the quiz.
   * ====================================================================== */
  var TEST_ANSWERS = {
    q1: 'q1a', q2: 'q2b', q3: 'q3a', q4: 'q4a', q5: 'q5a',
    q6: ['q6a'], q7: ['q7a'], q8: 'q8a', q9: 'q9a'
  };

  function testApplyNoindex() {
    /* Page-scoped noindex. The theme's shared layout is NOT touched — this
       injects into <head> at runtime, which Googlebot honours because it
       indexes the rendered DOM. Crawlers that do not run JS will not see it,
       which is acceptable: the page is unlinked from the entire store. */
    if (document.querySelector('meta[data-kidslar-test-robots]')) return;
    var meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', 'noindex, nofollow, noarchive');
    meta.setAttribute('data-kidslar-test-robots', '');
    document.head.appendChild(meta);
  }

  function testMaybeJumpToOffer(app) {
    var params;
    try {
      params = new URLSearchParams(global.location.search);
    } catch (error) {
      return;
    }
    if (params.get('kq_offer') !== '1') return;

    app.state = freshState(app.branch.id);
    app.state.answers = JSON.parse(JSON.stringify(TEST_ANSWERS));
    app.state.flags.started = true;
    app.state.flags.completed = true;
    app.state.screen = 'result';
    app.profile = buildProfile(app.state);
    saveState(app.state);

    var flow = RESULT_FLOWS[app.profile.diagnosis] || [];
    var index = 0;
    for (var i = 0; i < flow.length; i++) {
      if (flow[i].type === 'offer') { index = i; break; }
    }
    app.renderResultStep(index);
  }

  var instances = new WeakMap();

  function mount(root) {
    if (!root || instances.has(root)) return;
    var app = new QuizApp(root);
    instances.set(root, app);
    testApplyNoindex();
    testMaybeJumpToOffer(app);
  }

  function unmount(root) {
    var instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll('[data-kidslar-quiz]').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll();
    });
  } else {
    mountAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-kidslar-quiz]').forEach(unmount);
  });

  Core.mount = mount;
  Core.mountAll = mountAll;
})(typeof window !== 'undefined' ? window : globalThis);
